import { ComparisonRow } from '../comparisonRows'
import type { CompensationImportContext } from '../domains/payouts/compensation-import'
export type CompensationRuleVersionTransition =
  | { action: 'schedule'; versionId: number; end: string }
  | { action: 'cancel' | 'delete'; versionId: number }
  | {
      action: 'publish'
      compensationRuleId: number
      start: string
      end?: string
      diagram: Json
      isTvpBased?: boolean
      visibleIfScheduled?: boolean
      confirmedVersionId?: number
      confirmedDeactivationDate?: string
    }
import {
  ActivitiesWithDeals,
  DataIssuesWithChildrenInsert,
  DBFunctionReturns,
  InvoicesWithDeals,
  NotificationsQuery,
  ProfileWithJoins
} from '@dolfin/business/custom_types'
import {
  CompositeTypes,
  Database,
  Enums,
  Json,
  Tables,
  TablesInsert,
  TablesUpdate
} from '@dolfin/business/db_types'

import {
  MetricValueToDelete,
  ParsedExecutionResult,
  PayoutReleaseContext,
  PayoutRelease,
  PayoutWithSource,
  RecordData,
  CreditedTargetEvidence
} from '@dolfin/business/types'
import type {
  DisputeAnalyticsIssueRow,
  DisputeAnalyticsStatus,
  DisputeAnalyticsTeamLookup,
  DisputeAnalyticsTeamReference,
  DisputeTag,
  GetDisputeThreadParams
} from '../domains/disputes'
export type {
  DisputeAnalyticsIssueRow,
  DisputeAnalyticsTagCount,
  DisputeAnalyticsTeamLookup,
  DisputeAnalyticsTeamReference,
  DisputeTag
} from '../domains/disputes'

export type TeamMembershipInPeriod = {
  profileId: string
  teamId: number
  activeFrom: string
  activeUntil: string
}

export type MetricPreviewRecipient = Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>

export type MetricPreviewRecipientPage = {
  recipients: MetricPreviewRecipient[]
  total: number
}

export type PayoutCalculationContextResponse =
  | {
      status: 'available'
      capturedAt: string
      schemaVersion: number
      context: Record<string, unknown>
    }
  | { status: 'unavailable' }
  | { status: 'not_applicable' }

export type ProfileOrganizationActivePeriod = {
  activeFrom: string
  activeUntil: string | null
}

export type ActiveCompensationRuleVersion = {
  version_id: number
  id: number
  label: string
  description: string
  source: Tables<'compensation_rules'>['source']
  type: Tables<'compensation_rules'>['type']
  outcome_period: Tables<'compensation_rules'>['outcome_period']
  user_field: Tables<'compensation_rules'>['user_field']
  accrual_date_field: Tables<'compensation_rules'>['accrual_date_field']
  kpis_table_id: Tables<'compensation_rules'>['kpis_table_id']
  is_tvp_based: Tables<'compensation_rule_versions'>['is_tvp_based']
  active_period: Tables<'compensation_rule_versions'>['active_period']
  diagram: Tables<'compensation_rule_versions'>['diagram']
}

export type MetricValueCurrencyConversionKind = 'converted' | 'zero' | 'fallback'

export type DataIngestionResource = 'deals' | 'invoices' | 'activities'

export type DataIngestionRpcResult = {
  operation: 'created' | 'updated' | 'unchanged'
  recalculation: 'scheduled' | 'not_required'
  warnings?: Json
}
export type DomainEventDelivery =
  Database['public']['Functions']['claim_domain_event_deliveries']['Returns'][number]

export type CompleteDomainEventDeliveryParams = {
  deliveryId: number
  status: 'delivered' | 'skipped' | 'failed'
  error?: string | null
  skippedReason?: string | null
  retryAfterSeconds?: number
}
export type CompensationTierSnapshotWrite = {
  organizationId: number
  profileId: string
  compensationRuleId: number
  compensationRuleVersionId: number
  tierNodeId: string
  referenceType: 'target' | 'metric' | 'column'
  referenceKey: string
  targetId?: number | null
  periodStart: string
  periodEnd: string
  snapshotKey: string
  tierConfigHash: string
  payload: Json
  attainment: number
  currentTierIndex: number
  nextTierIndex?: number | null
  isTopTier: boolean
  totalAchievement: number
  totalAchievementPercentage: number
  gapToNextTierVsTierSpan?: number | null
  progressWithinCurrentTierWindow?: number | null
  lastCalculatedAt?: string
}

export type CompensationTierEvent = {
  eventId: number
  eventType: 'compensation_tier_entered' | 'compensation_tier_progress_updated'
  organizationId: number
  profileId: string
  payload: Json
  occurredAt: string
}

export type DataIngestionBatchContext = {
  existing: Record<string, boolean>
  profilesByEmail: Record<string, string>
  profilesById: Record<string, string>
  dealsByExternalId: Record<string, number>
  lineItemsByExternalId: Record<string, { id: number; invoice_id: number | null }>
  dealLineItemsByExternalId?: Record<string, { id: number; deal_id: number }>
}

export type DataIngestionBatchRpcResult = Omit<DataIngestionRpcResult, 'operation'> & {
  index: number
  external_id: string
  outcome: 'accepted' | 'updated' | 'rejected'
  operation: 'created' | 'updated' | 'unchanged' | 'rejected'
  errors?: Json
}

export type MetricValueCurrencySnapshot = Omit<
  Pick<
    TablesInsert<'metric_value_currencies'>,
    'metric_value_id' | 'currency' | 'value' | 'exchange_rate' | 'converted_at' | 'conversion_kind'
  >,
  'conversion_kind'
> & {
  conversion_kind: MetricValueCurrencyConversionKind
}

export type MetricValueCurrencyBackfillProgress = Tables<'metric_value_currency_backfills'>
export type MetricValueCurrencyBackfillPage =
  DBFunctionReturns<'get_metric_values_for_currency_backfill'>
export type MetricValueCurrencyReconciliationChunk =
  DBFunctionReturns<'reconcile_metric_value_currency_dimensions_chunk'>[number]
export type MetricValueCurrencyProgressUpdate = Partial<
  Pick<
    MetricValueCurrencyBackfillProgress,
    | 'status'
    | 'phase'
    | 'cursor_metric_value_id'
    | 'processed_count'
    | 'dimensioned_count'
    | 'converted_count'
    | 'zero_count'
    | 'fallback_count'
    | 'rejected_count'
    | 'persistence_failure_count'
    | 'retry_count'
    | 'last_error'
  >
>

export type CreditSetupListParams = {
  organizationId: number
  sourceMetricId: number
  includeArchived?: boolean
}

export type CreditSetupListByMetricIdsParams = {
  organizationId: number
  sourceMetricIds: number[]
  includeArchived?: boolean
}

export type CreditSetupUpdateParams = {
  id: number
  organizationId: number
  sourceMetricId: number
  update: TablesUpdate<'credit_setups'>
}

export type CreditSetupUsage = {
  targetIds: number[]
  compensationRuleVersionIds: number[]
  dependencyCount: number
}

export type CreditSetupRuleListParams = {
  organizationId: number
  creditSetupId: number
  includeArchived?: boolean
}

export type CreditSetupRulePageParams = CreditSetupRuleListParams & {
  search: string
  offset: number
  limit: number
}

export type CreditSetupRulePage = {
  items: Tables<'credit_setup_rules'>[]
  nextOffset: number | null
}

export type CreditSetupRuleUpdateParams = {
  id: number
  organizationId: number
  creditSetupId: number
  update: TablesUpdate<'credit_setup_rules'>
}

export type CreditSetupRuleAssignmentListParams = {
  organizationId: number
  creditSetupId: number
  creditSetupRuleId?: number
}

export type CreditSetupRuleAssignmentBatchParams = {
  organizationId: number
  creditSetupRuleIds: number[]
}

export type CreditSetupRuleUsageParams = CreditSetupRuleAssignmentListParams & {
  creditSetupRuleId: number
}

export type CreditSetupRuleAssignmentUpdateParams = {
  assignmentId: number
  organizationId: number
  creditSetupId: number
  creditSetupRuleId: number
  effectiveTo: string
}

export type CreditSetupRuleAssignmentEditParams = Omit<
  CreditSetupRuleAssignmentUpdateParams,
  'effectiveTo'
> & {
  effectiveFrom: string
  effectiveTo: string | null
}

export type CreditSetupRuleAssignmentReplaceParams = {
  organizationId: number
  creditSetupId: number
  /** The successor Rule. The existing assignment identifies the prior Rule. */
  creditSetupRuleId: number
  profileId: string | null
  teamId: number | null
  effectiveFrom: string
  effectiveTo: string | null
  existingAssignmentId: number | null
}

export type CreditSetupRuleAssignment = Tables<'credit_setup_rule_assignments'> & {
  organization_id: number
  /** Derived through the Assignment's Rule to preserve the RPC read contract. */
  credit_setup_id: number
  credit_setup_rule_id: number
}

export type TargetAchievementRow = DBFunctionReturns<'get_user_targets_achievement'>[number] & {
  company_target?: boolean | null
  credit_setup_id?: number | null
}

export type TargetWithCreditSetup = Tables<'targets'>

export type TargetCreditSetupSelectionParams = {
  targetId: number
  creditSetupId: number | null
}

export type TargetUpsertWithCreditSetupSelectionParams = {
  target: TablesInsert<'targets'>
  creditSetupId: number | null
}
import {
  CloseImpersonationParams,
  CreateImpersonationAttemptParams,
  CreateImpersonationAttemptResult,
  ImpersonationAuditLog,
  ImpersonationCandidateProfile,
  ImpersonationProfile,
  ListImpersonationCandidateProfilesParams
} from '../domains/impersonation/types'
import { MetricSourceRecord, MetricUsage } from '../domains/metrics'
import type { MetricReference } from '../domains/metrics/references'
import type {
  RelationalCustomFieldQuerySource,
  RelationalCustomFieldSourceRole,
  ValidatedRelationalCustomFieldFilter,
  ValidatedRelationalCustomFieldSort
} from '../record'
import type { DealGridRequest, DealGridResult } from '../domains/deals'
import type { ActivityGridRequest } from '../domains/activities'
import type { InvoiceGridRequest } from '../domains/invoices'
import type { SourceGridResult } from '../domains/source-grid'
import type { KpiGridRequest } from '../domains/kpis'
import type { CustomRecordGridRequest } from '../domains/custom_records'
import type {
  GeneratedPayoutsAnalytics,
  ManualPayoutsAnalytics,
  PayoutAdjustmentsAnalytics,
  PayoutAdjustmentBreakdown,
  PayoutAnalytics,
  PayoutAnalyticsParams,
  PayablePayoutsAnalytics
} from '../domains/payouts/analytics'
import type { GetProfilesParams } from '../domains/profiles'
import { TargetUsage } from '../domains/targets'
import { TeamDeletionBlockers, TeamUsage } from '../domains/teams'

export type TargetContributingRecordsParams = {
  metricId: number
  organizationId: number
  periodStart: string
  periodEnd: string
  source: Enums<'plan_source'>
  profileId?: string
  excludedProfileId?: string
  teamIds?: number[]
  targetCurrency?: string
  offset?: number
  limit?: number
}

export type TargetContributingRecordsPage = {
  records: Array<Tables<'metric_values'> & Record<string, unknown>>
  count: number
}

export type TargetContributingSourceRecord = MetricSourceRecord & {
  profile_id: string | null
  profiles: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'> | null
}

export type TargetContributingSourceRecordsPage = {
  records: TargetContributingSourceRecord[]
  count: number
}

export type MetricSourceRecordsPage = {
  records: MetricSourceRecord[]
  count: number
}

export type MetricTargetScopeAccess = {
  metric_id: number
  company_target: boolean
  global_target: boolean
  team_target: boolean
  contributing_records_visibility: string | null
  target_team_ids: number[]
  active_target_team_ids: number[]
}

export type TargetContributingSourceRecordsPageParams = {
  scope: 'all' | 'own' | 'other'
  included: boolean
  search?: string
  offset: number
  limit: number
}

export type TargetContributingRecordsAccessParams = {
  requesterId: string
  displayedProfileId: string
  organizationId: number
}

export type TargetContributingRecordsAccess = {
  canAccess: boolean
  isAdmin: boolean
}

export type TargetCreditedEvidenceSummary = {
  creditSetupId: number
  creditSetupRuleId: number | null
  assignmentId: number | null
  profileId: string | null
  teamId: number | null
  contributionCount: number
}

export type TargetCreditSetupSegment = {
  targetId: number
  metricId: number
  consumerKind: 'target'
  creditSetupId: number
  creditSetupRuleId: number | null
  assignmentId: number | null
  profileId: string
  teamId: number | null
  effectiveFrom: string
  effectiveTo: string
  noRuleApplied: boolean
}

export type TargetContributingRecordsResult = {
  ownRecords: TargetContributingRecordsPage
  otherRecords: TargetContributingRecordsPage | null
  ownSourceRecords: TargetContributingSourceRecord[]
  otherSourceRecords: TargetContributingSourceRecord[] | null
  allSourceRecords: TargetContributingSourceRecord[] | null
  requesterProfileId: string
  contributionProfileId: string
  displayedProfileContribution: number | null
  remainingContribution: number | null
  /** @deprecated Use displayedProfileContribution. Kept for API consumers during the transition. */
  requesterContribution: number | null
  /** @deprecated Use remainingContribution. Kept for API consumers during the transition. */
  otherUsersContribution: number | null
  sharedContribution: number | null
  recordDetailScope: 'own' | 'all'
  creditedContributionDetails?: CreditedTargetEvidence[]
  sourceRecordsPage?: TargetContributingSourceRecordsPage
  teamIds?: number[]
  metric?: MetricReference
  targetAchievement?: DBFunctionReturns<'get_user_targets_achievement'>[number]
  creditedEvidence?: TargetCreditedEvidenceSummary[]
}

export type ValidationProcessReviewerSummary = {
  profileId: string
  name: string | null
  email: string | null
  pendingCount: number
}

export type CustomFieldFilterCandidateIdsParams = {
  organizationId: number
  source: Enums<'plan_source'>
  customFieldId: number
  sourceRole: RelationalCustomFieldSourceRole
  virtualTableId?: number
  maxResults?: number
} & (
  | {
      valueUserId: string
      valueDateEnd?: never
      valueDateStart?: never
    }
  | {
      valueDateEnd: string
      valueDateStart: string
      valueUserId?: never
    }
)

export type ValidationProcessPendingPayoutSample = {
  id: number
  profileId: string
  recipientName: string | null
  recipientEmail: string | null
  value: number | null
  currency: string | null
  effectiveDate: string | null
  periodStart: string | null
  periodEnd: string | null
  status: Tables<'payouts'>['status']
  nextReviewerIds: string[]
}

export type RelationalCustomFieldQueryIdsParams = {
  organizationId: number
  source: RelationalCustomFieldQuerySource
  filters: ValidatedRelationalCustomFieldFilter[]
  sort?: ValidatedRelationalCustomFieldSort | null
  offset: number
  limit: number
  virtualTableId?: number
}

export type RelationalCustomFieldQueryIdsResult = {
  recordIds: number[]
  totalCount: number
}

export type GetDealsGridParams = DealGridRequest

export type ValidationProcessPendingStatementSample = {
  id: number
  profileId: string
  recipientName: string | null
  recipientEmail: string | null
  periodStart: string
  periodEnd: string
  status: Tables<'statements'>['status']
  nextReviewerIds: string[]
}

export type StartStatementsReviewResult = {
  updatedStatementIds: number[]
  skippedStatementIds: number[]
}

export type ChangeStatementPaymentStatusParams = {
  organizationId: number
  statementId: number
} & (
  | { expectedStatus: Exclude<Tables<'statements'>['status'], 'paid'>; status: 'paid' }
  | { expectedStatus: 'paid'; status: 'open' | 'approved' }
)

export type StatementResetEligibility = {
  statementId: number
  canReset: boolean
  reason: 'not_in_review' | 'discrepancies' | null
}

export type StatementReviewFlowRow = Pick<
  Tables<'statements'>,
  'id' | 'profile_id' | 'period_start' | 'status' | 'has_pending_payouts'
> & {
  payouts: Pick<Tables<'payouts'>, 'id'>[]
}

export type GetStatementMonthRowsParams = {
  organizationId: number
  managedTeamIds: number[] | null
  startDate: string
  endDate: string
  afterId?: number
  limit: number
}

export type StatementMonthRow = Tables<'statements'> & {
  owner: Pick<Tables<'profiles'>, 'id' | 'name' | 'email'> | null
  pending_approval: boolean | null
  issueCount: number
  nextApproverIds: string[]
}

export type StatementReadPayout = Tables<'payouts'> & {
  execution: Pick<Tables<'executions'>, 'created_at'> | null
}

export type StatementReadRelations = {
  payouts: StatementReadPayout[]
  payoutInstallments: (Tables<'payout_installments'> & { payouts: StatementReadPayout })[]
  approvals: (Tables<'statement_approvals'> & {
    approver: Pick<Tables<'profiles'>, 'id' | 'name' | 'email'> | null
  })[]
  issues: Tables<'issues'>[]
}

export type StatementReadChanges = {
  manualAdjustmentHistory: PayoutAdjustmentBreakdown[]
  updatesOnHold: UpdateOnHoldReviewRow[]
}

export type MonthlyStatementReviewFlowSummary = {
  totalCount: number
  payoutValidationFlow: boolean
  statementApprovalFlow: boolean
  counts: {
    pendingApproval: number
    inReview: number
    approved: number
    paid: number
  }
  pendingReviewStatementIds: number[]
  firstInReviewStatement: {
    id: number
    profileId: string
    periodStart: string
  } | null
  readyToPayStatementIds: number[]
}

export type ValidationProcessFlowSummary<TSample> = {
  configured: boolean
  enabled: boolean
  validationSteps: number
  totalCount: number
  completedCount: number
  pendingCount: number
  notReadyCount?: number
  pendingReviewerSummaries: ValidationProcessReviewerSummary[]
  pendingSamples: TSample[]
  notReadySamples?: TSample[]
  completedSamples: TSample[]
  reviewerAggregationPartial: boolean
}

export type ValidationProcessOrganizationConfig = {
  organizationId: number
  payoutValidationFlow: boolean
  statementApprovalFlow: boolean
}

export type ValidationProcessStepEntity = 'compensations_validation' | 'statements_approval'

export type ValidationProcessReviewerRow = {
  id: number
  nextReviewerIds: string[]
}

export type ValidationProcessRawPayoutSample = {
  id: number
  profileId: string
  recipientName: string | null
  recipientEmail: string | null
  value: number | null
  currency: string | null
  effectiveDate: string | null
  periodStart: string | null
  periodEnd: string | null
  status: Tables<'payouts'>['status']
  nextReviewerIds: string[]
}

export type ManualPayoutExchangeRateMethod = 'organization_settings' | 'custom'

export type ManualPayoutUpsertPayload = Pick<
  TablesInsert<'payouts'>,
  | 'category_id'
  | 'compensation'
  | 'converted_at'
  | 'currency'
  | 'description'
  | 'effective_date'
  | 'exchange_rate'
  | 'manual_exchange_rate_from_currency'
  | 'manual_exchange_rate_method'
  | 'manual_exchange_rate_source'
  | 'organization_id'
  | 'original_value'
  | 'period_end'
  | 'period_start'
  | 'profile_id'
  | 'statement_id'
  | 'type'
  | 'value'
> & {
  id?: number
}

export type ValidationProcessRawStatementSample = {
  id: number
  profileId: string
  recipientName: string | null
  recipientEmail: string | null
  periodStart: string
  periodEnd: string
  status: Tables<'statements'>['status']
  nextReviewerIds: string[]
}

export type ValidationProcessOverview = {
  organizationId: number
  flowsConfigured: {
    payoutValidation: boolean
    statementApproval: boolean
  }
  filters: {
    payoutMonthStarts: string[] | null
    statementPeriodStart: string | null
    statementPeriodEnd: string | null
  }
  payoutValidation: ValidationProcessFlowSummary<ValidationProcessPendingPayoutSample> | null
  statementApproval: ValidationProcessFlowSummary<ValidationProcessPendingStatementSample> | null
}

export type GetValidationProcessOverviewParams = {
  organizationId: number
  payoutMonthStarts?: string[]
  statementPeriodStart?: string
  statementPeriodEnd?: string
  sampleLimit?: number
}

export type GetValidationProcessPayoutParams = {
  organizationId: number
  payoutMonthStarts?: string[]
}

export type GetValidationProcessStatementParams = {
  organizationId: number
  statementPeriodStart?: string
  statementPeriodEnd?: string
}

export type GetValidationProcessRowsParams = {
  limit: number
}

export type StatementDiscrepancyRevertResult = {
  targetStatementReopened: boolean
}

export type ProfileListItem = Omit<Tables<'profiles'>, 'invitations' | 'last_signed_in_at'>

export type ProfilesResult = {
  items: ProfileListItem[]
  count: number | null
}

export type ConnectedAppUserMappingBackfillResult =
  DBFunctionReturns<'backfill_connected_app_user_mappings'>[number] &
    DBFunctionReturns<'backfill_connected_app_custom_record_user_mappings'>[number]

export type ProviderProfileIdentity = {
  organizationId: number
  profile: ProfileListItem
}

export type OrganizationReference = Pick<Tables<'organizations'>, 'id' | 'name'>

export type OrganizationSearchResult = Pick<Tables<'organizations'>, 'id' | 'name'>

export type OrganizationProfileSearchResult = Pick<Tables<'profiles'>, 'id' | 'name' | 'email'>

export type OrganizationTeamSearchResult = Pick<Tables<'teams'>, 'id' | 'name'>
export type AdminOrganizationMrrOverviewRow = {
  id: number
  name: string | null
  currency: 'EUR'
  seatCount: number
  included: boolean
  pricePerSeat: number
  mrr: number
}

export type AdminOrganizationMrrSettingInput = {
  organizationId: number
  included: boolean
  pricePerSeat: number
}

export type AdminOrganizationMrrSetting = Tables<'organization_mrr_settings'>

export type CreditSetupRuleAssignmentSubject =
  | {
      type: 'profile'
      id: string
      name: string | null
      email: string | null
    }
  | {
      type: 'team'
      id: number
      name: string
      memberCount: number
      members: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>[]
    }

export type CreditSetupRuleAssignmentSubjectPage = {
  items: CreditSetupRuleAssignmentSubject[]
  nextOffset: number | null
}

export type OrganizationPlanSearchResult = Pick<Tables<'plans'>, 'id' | 'label'>

export type OrganizationTargetSearchResult = Pick<Tables<'targets'>, 'id' | 'label' | 'plan_id'>

export type CompensationRecordType = 'deal' | 'payout' | 'statement' | 'dispute' | 'discrepancy'

export type CompensationRecordSearchParams = {
  organizationId: number
  recordType: CompensationRecordType
  query: string
  profileId?: string
  status?: string
  periodStart?: string
  periodEnd?: string
  limit: number
}

export type CompensationRecordSearchMatch = {
  type: CompensationRecordType
  id: number
  label: string
  status: string | null
  profileId: string | null
  profileName: string | null
  amount: number | null
  currency: string | null
  date: string | null
  periodStart: string | null
  periodEnd: string | null
  related: {
    dealId: number | null
    payoutId: number | null
    statementId: number | null
  }
}

export type SubscriptionCompensationOverviewPeriod = 12 | 24

export type SubscriptionCompensationOverviewFilters = {
  statuses?: string[]
  compensationRuleIds?: number[]
  search?: string
}

export type GetSubscriptionCompensationSourceRowsParams = {
  organizationId: number
  profileId?: string
  months: SubscriptionCompensationOverviewPeriod
  referenceDate?: string
  filters?: SubscriptionCompensationOverviewFilters
}

export type HasRecentSubscriptionDataParams = {
  organizationId: number
  profileId: string
}

export type SubscriptionCompensationSourceRow = {
  payoutId: number
  organizationId: number
  profileId: string
  amount: number
  currency: string | null
  effectiveDate: string
  statementPeriodStart: string | null
  statementPeriodEnd: string | null
  compensationRuleId: number | null
  compensationRuleLabel: string | null
  source: 'invoice' | 'line_item'
  invoiceId: number | null
  lineItemId: number | null
  dealId: number | null
  invoiceEffectiveAt: string | null
  subscriptionId: string
  subscriptionStartDate: string | null
  subscriptionEndDate: string | null
  subscriptionStatus: string | null
  customerId: number | null
  providerCustomerId: string | null
  customerDisplayName: string | null
  accountName: string | null
}

export type SubscriptionCompensationRuleBreakdown = {
  compensationRuleId: number | null
  compensationRuleLabel: string
  total: number
  detailHref: string | null
  dealId: number | null
  payoutId: number | null
}

export type SubscriptionCompensationCell = {
  month: string
  total: number
  breakdown: SubscriptionCompensationRuleBreakdown[]
}

export type SubscriptionCompensationMonthSummary = {
  month: string
  label: string
  total: number
  accountCount: number
  subscriptionCount: number
  newAccountCount: number
  lostAccountCount: number
  newSubscriptionCount: number
  lostSubscriptionCount: number
}

export type SubscriptionCompensationSubscriptionRow = {
  accountName: string
  subscriptionId: string
  status: string | null
  customerId: number | null
  providerCustomerId: string | null
  customerDisplayName: string | null
  visibleTotal: number
  wholeSubscriptionTotal: number
  startDate: string | null
  endDate: string | null
  isOngoing: boolean
  continuesBeforeVisibleRange: boolean
  continuesAfterVisibleRange: boolean
  averageVisibleMonthlyTotal: number
  cells: SubscriptionCompensationCell[]
}

export type SubscriptionCompensationAccountGroup = {
  accountName: string
  subscriptionCount: number
  monthlyTotals: Array<{
    month: string
    total: number
  }>
  subscriptions: SubscriptionCompensationSubscriptionRow[]
}

export type SubscriptionCompensationOverview = {
  organizationId: number
  profileId: string | null
  periodMonths: SubscriptionCompensationOverviewPeriod
  generatedAt: string
  months: SubscriptionCompensationMonthSummary[]
  accountGroups: SubscriptionCompensationAccountGroup[]
  filters: SubscriptionCompensationOverviewFilters
}

export type BusinessUnitListItem = Tables<'business_units'> & {
  assigned_users_count: number
  cost_centers: (Tables<'business_unit_cost_centers'> & {
    assigned_users_count: number
    department: Tables<'departments'>
    legal_entity: Tables<'legal_entities'> | null
    location: Tables<'locations'> | null
  })[]
}

export type BusinessUnitRemovalResult = {
  action: 'deleted' | 'archived'
  business_unit_id: number
  archived_at: string | null
}

export type BusinessUnitTimelineHistoryRow = {
  row_id: number
  business_unit_id: number | null
  business_unit_cost_center_id: number | null
  business_unit_name: string | null
  business_unit_cost_center: string | null
  start_date: string
  end_date: string | null
}

export type BusinessUnitTimelinePreviewModification = {
  id: number | null
  modification_type: string
  original_start_date: string | null
  original_end_date: string | null
  new_start_date: string | null
  new_end_date: string | null
  affected_start_date: string | null
  affected_end_date: string | null
}

export type BusinessUnitTimelinePreviewResult = {
  history_rows: BusinessUnitTimelineHistoryRow[]
  modifications: BusinessUnitTimelinePreviewModification[]
  controls: {
    can_extend_previous: boolean
    can_bring_forward_next: boolean
    show_end_date: boolean
  }
}

export type CurrentOrganizationProfileData = Pick<
  Tables<'organizations_profiles_data'>,
  | 'organization_id'
  | 'profile_id'
  | 'from_date'
  | 'type'
  | 'total_variable_pay'
  | 'business_unit_id'
  | 'business_unit_name'
  | 'business_unit_cost_center_id'
  | 'business_unit_cost_center'
  | 'business_unit_department'
  | 'business_unit_legal_entity'
  | 'business_unit_location_country'
  | 'business_unit_location_city'
>

export type ValueDiscrepancyAdjustmentInput = {
  description?: TablesInsert<'payouts'>['description']
  effective_date: NonNullable<TablesInsert<'payouts'>['effective_date']>
  statement_id: NonNullable<TablesInsert<'payouts'>['statement_id']>
}

export type PayoutDiscrepancyRecalculationReason = {
  discrepancy_id: number
  recalculation_reason: Enums<'recalculation_reason'> | null
  created_at: string | null
  match_scope: 'execution' | 'record_exact' | 'period_exact' | 'related_rule_profile' | null
}

export type UpdateOnHoldDecision =
  | 'keep_current'
  | 'update_calculation'
  | 'next_statement'
  | 'update_calculation_keep_adjustment'

export type UpdateOnHoldDecisionInput = {
  updateId: number
  decision: UpdateOnHoldDecision
}

export type UpdateOnHoldBlocker = {
  updateId: number
  code: string
  message?: string
}

export type UpdateOnHoldPreview = {
  fingerprint: string
  lines: Record<string, unknown>[]
  summary: Record<string, unknown>
  blockers: UpdateOnHoldBlocker[]
}

export type UpdateOnHoldCommittedResult = Omit<UpdateOnHoldPreview, 'fingerprint'> & {
  batchId: string | null
  resolvedBy?: string | null
  resolvedByName?: string | null
  resolvedAt?: string | null
}

type UpdateOnHoldReviewPayout = Pick<
  Tables<'payouts'>,
  | 'id'
  | 'status'
  | 'currency'
  | 'original_value'
  | 'description'
  | 'compensation'
  | 'edited'
  | 'exchange_rate'
  | 'compensation_rule_id'
  | 'profile_id'
  | 'period_start'
  | 'period_end'
  | 'deal_id'
  | 'invoice_id'
  | 'activity_id'
  | 'kpi_id'
  | 'line_item_id'
  | 'custom_record_id'
  | 'statement_id'
> & {
  execution?: Pick<Tables<'executions'>, 'compensation_rule_version_id'> | null
  profiles?: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'> | null
  compensation_rules?: {
    id?: number
    label?: string
    source: Enums<'plan_source'>
    compensation_rule_versions?:
      | Pick<Tables<'compensation_rule_versions'>, 'id' | 'active_period' | 'created_at'>[]
      | null
  } | null
  deals?: { crm_provider: string | null } | null
  invoices?: {
    payroll_provider: string | null
    deals?: { crm_provider: string | null } | null
  } | null
  activities?: { deals?: { crm_provider: string | null } | null } | null
  line_items?: {
    invoices?: {
      payroll_provider: string | null
      deals?: { crm_provider: string | null } | null
    } | null
  } | null
  statements?: Pick<Tables<'statements'>, 'id' | 'period_start' | 'period_end' | 'status'> | null
  payout_installments?: Pick<Tables<'payout_installments'>, 'statement_id' | 'status'>[] | null
}

type UpdateOnHoldStatementSummary = Pick<
  Tables<'statements'>,
  'id' | 'period_start' | 'period_end' | 'status'
>

type UpdateOnHoldScaffoldPayout = Pick<Tables<'payouts'>, 'id' | 'status'> & {
  profiles?: Pick<Tables<'profiles'>, 'id' | 'name'> | null
  compensation_rules?: { source: Enums<'plan_source'> } | null
  deals?: { crm_provider: string | null } | null
  invoices?: {
    payroll_provider: string | null
    deals?: { crm_provider: string | null } | null
  } | null
  activities?: { deals?: { crm_provider: string | null } | null } | null
  line_items?: {
    invoices?: {
      payroll_provider: string | null
      deals?: { crm_provider: string | null } | null
    } | null
  } | null
  statements?: UpdateOnHoldStatementSummary | null
  payout_installments?: Pick<Tables<'payout_installments'>, 'statement_id' | 'status'>[] | null
}

/** Lightweight projection used to construct large Updates on hold accordions. */
export type UpdateOnHoldScaffoldRow = Pick<
  Tables<'payout_discrepancies'>,
  'id' | 'payout_id' | 'discrepancy_type' | 'protected_reason' | 'protected_status' | 'created_at'
> & {
  payouts?: UpdateOnHoldScaffoldPayout | null
  existing_statement?: UpdateOnHoldStatementSummary | null
  calculated_statement?: UpdateOnHoldStatementSummary | null
}

/** The selected projection returned by the Updates on hold review query. */
export type UpdateOnHoldReviewRow = Pick<
  Tables<'payout_discrepancies'>,
  | 'id'
  | 'payout_id'
  | 'discrepancy_type'
  | 'calculated_value'
  | 'existing_value'
  | 'existing_original_value'
  | 'calculated_node_output'
  | 'existing_currency'
  | 'protected_reason'
  | 'protected_status'
  | 'execution_id'
  | 'created_at'
> &
  Partial<
    Omit<
      Tables<'payout_discrepancies'>,
      | 'id'
      | 'payout_id'
      | 'discrepancy_type'
      | 'calculated_value'
      | 'existing_value'
      | 'existing_original_value'
      | 'calculated_node_output'
      | 'existing_currency'
      | 'protected_reason'
      | 'protected_status'
      | 'execution_id'
      | 'created_at'
    >
  > & {
    calculation_execution?: Pick<Tables<'executions'>, 'compensation_rule_version_id'> | null
    payouts?: UpdateOnHoldReviewPayout | null
    existing_statement?: Pick<
      Tables<'statements'>,
      'id' | 'period_start' | 'period_end' | 'status'
    > | null
    calculated_statement?: Pick<
      Tables<'statements'>,
      'id' | 'period_start' | 'period_end' | 'status'
    > | null
  }

export type UpdateOnHoldReviewItem = {
  update: UpdateOnHoldReviewRow & {
    /** Derived from the existing adjustment rows and payout audit log. */
    manual_adjustment_active: boolean
    manual_adjustment_delta: number | null
    manual_adjustment_delta_reliable: boolean
    manual_adjustment_original_delta: number | null
  }
  payout: Tables<'payouts'> | null
  profile: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'> | null
  sourceLabel: string | null
  /** Integration that supplied the source data that triggered the update. */
  sourceProvider: string | null
  currentStatement: Pick<
    Tables<'statements'>,
    'id' | 'period_start' | 'period_end' | 'status'
  > | null
  calculatedStatement: Pick<
    Tables<'statements'>,
    'id' | 'period_start' | 'period_end' | 'status'
  > | null
  nextAvailableStatement?: {
    statement_id: number | null
    period_start: string
    period_end: string
    status: Enums<'statement_status'> | null
  } | null
  reason: PayoutDiscrepancyRecalculationReason | null
  /** Version snapshot shown only for a compensation-rule version recalculation. */
  compensationRuleVersionChange?: {
    ruleLabel: string
    usedVersion: number
    latestVersion: number
  } | null
  /** Meaningful source-audit field changes matched to existing recalculation history. */
  dataChanges?: MeaningfulAuditChange[]
  /** A statement-movement update can only be dismissed while its current statement is paid. */
  paidStatementLocked?: boolean
  availableDecisions: UpdateOnHoldDecision[]
}

export type UpdateOnHoldScaffoldItem = {
  update: Pick<
    UpdateOnHoldScaffoldRow,
    'id' | 'payout_id' | 'discrepancy_type' | 'protected_reason' | 'protected_status' | 'created_at'
  >
  payout: UpdateOnHoldScaffoldPayout | null
  profile: Pick<Tables<'profiles'>, 'id' | 'name'> | null
  sourceLabel: string | null
  sourceProvider: string | null
  currentStatement: UpdateOnHoldStatementSummary | null
  calculatedStatement: UpdateOnHoldStatementSummary | null
  reason: PayoutDiscrepancyRecalculationReason | null
}

export type UpdatesOnHoldListResponse =
  | {
      mode: 'eager'
      totalCount: number
      items: UpdateOnHoldReviewItem[]
    }
  | {
      mode: 'progressive'
      totalCount: number
      scaffoldItems: UpdateOnHoldScaffoldItem[]
    }

export type UpdatesOnHoldGroupPageResponse = {
  items: UpdateOnHoldReviewItem[]
}

export type RecordInvestigationSource = Extract<
  Enums<'plan_source'>,
  'activity' | 'deal' | 'deal_line_item' | 'invoice' | 'kpi' | 'line_item'
>

export type RecordRecalculationHistoryEntry = {
  id: number
  records_to_be_processed_id: number
  created_at: string
  recalculation_reason: Enums<'recalculation_reason'> | null
  source: RecordInvestigationSource
  record_id: number
}

export type MeaningfulAuditChange = {
  table_name: MeaningfulAuditChangeTableName
  record_id: number
  audit_row: {
    id: number
    changed_at: string
    old_data: Record<string, unknown> | null
    new_data: Record<string, unknown> | null
  }
  rows: ComparisonRow[]
  /** Profile records referenced by owner/profile audit fields, keyed by profile ID. */
  profileReferences?: Record<string, Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'>>
}

export type MeaningfulAuditChangeTableName =
  | 'activities_audit_log'
  | 'deals_audit_log'
  | 'invoices_audit_log'
  | 'kpis_audit_log'
  | 'line_items_audit_log'
  | 'deal_line_items_audit_log'
  | 'custom_records_audit_log'
  | 'metrics_audit_log'
  | 'payouts_audit_log'

export type PayoutRuleScopedMetricContribution = {
  source_id: number
  metric_id: number
  metric_label: string
  accrual_date: string | null
  metric_value: number | null
}

export type PayoutRuleMetricFieldReference = {
  field_key: string
  metric_label: string
}

export type TeamMembershipEvent = Pick<
  Tables<'teams_profiles_events'>,
  'profile_id' | 'team_id' | 'event_date' | 'status'
>

export type CurrentPlanTeamLink = Pick<Tables<'teams_plans'>, 'plan_id' | 'team_id'>

export type PlanTeamHistoryLink = CurrentPlanTeamLink & {
  id: number
  effective_from: string
  effective_to: string | null
}

export type CompensationRuleForExplanation = Tables<'compensation_rules'> & {
  plans:
    | (Pick<Tables<'plans'>, 'id' | 'label'> & {
        teams_plans_history: PlanTeamHistoryLink[]
      })
    | null
  compensation_rule_versions: Tables<'compensation_rule_versions'>[]
}

export type UpsertPlanTeamHistoryArgs = {
  organizationId: number
  historyId?: number | null
  teamId: number
  planId: number
  effectiveFrom: string
  effectiveTo?: string | null
}

export type ReplacePlanTeamHistoryArgs = {
  organizationId: number
  planId: number
  assignments: Array<Omit<UpsertPlanTeamHistoryArgs, 'organizationId'>>
  removedHistoryIds: number[]
}

export type LinkTeamToPlanArgs = {
  organizationId: number
  teamId: number
  planId: number
  effectiveFrom?: string
}

export type UnlinkTeamFromPlanArgs = {
  organizationId: number
  teamId: number
  planId: number
  effectiveOn?: string
}

export type TeamSetupSummaryRow = {
  team_id: number
  team_name: string
  description: string | null
  currency: string | null
  archived_at: string | null
  members_count: number
  managers_count: number
  full_view_users_count: number
  linked_plans_count: number
  linked_targets_count: number
}

export type TeamSetupMemberRow = {
  profile_id: string
  name: string | null
  email: string | null
  img_url: string | null
  team_id: number
  role: Enums<'team_role'>
  full_view_access: boolean
}

export type TeamSetupPlanSummaryRow = {
  team_id: number
  plan_id: number
  label: string
  archived: boolean
}

export type TeamSetupTargetSummaryRow = {
  team_id: number
  target_id: number
  label: string
  archived_at: string | null
}

export type TeamMembershipHistorySetupRow = {
  profile_id: string
  profile_name: string | null
  profile_email: string | null
  team_id: number
  team_name: string | null
  event_date: string
  status: Enums<'team_profile_status'>
  role: Enums<'team_role'> | null
  full_view_access: boolean | null
  effective_from: string | null
  effective_to: string | null
}

export type CurrentTeamMembershipSetupRow = {
  profile_id: string
  profile_name: string | null
  profile_email: string | null
  team_id: number
  team_name: string | null
  role: Enums<'team_role'>
  full_view_access: boolean
}

export type SaveUserEditTeamMembershipRangeSetupParams = {
  effectiveFromDate: string
  effectiveToDate?: string | null
  existingEffectiveFromDate?: string | null
  existingEffectiveToDate?: string | null
  existingTeamId?: number | null
  fullViewAccess: boolean
  organizationId: number
  profileId: string
  role: Enums<'team_role'>
  teamId: number
}

export type OrganizationSetupFlags = Pick<
  Tables<'organizations'>,
  | 'revenue_table'
  | 'revenue_field'
  | 'payout_validation_flow'
  | 'statement_approval_flow'
  | 'use_fixed_exchange_rates'
  | 'use_invoices_exchange_rates'
  | 'check_data_issues'
>

export type OrganizationMemberSetupRow = {
  profile_id: string
  name: string | null
  email: string | null
  role: Enums<'org_role'>
  archived: boolean
  teams: Array<{
    team_id: number
    team_name: string | null
    team_role: Enums<'team_role'> | null
  }>
}

export type OrganizationInvitationSetupRow = Pick<
  Tables<'invitations'>,
  | 'invitation_id'
  | 'email'
  | 'name'
  | 'organization_id'
  | 'role'
  | 'status'
  | 'team_id'
  | 'team_role'
  | 'start_date'
  | 'created_at'
>

export type TeamProfileRow = {
  profile_id: string
  profiles?: {
    email?: string | null
    img_url?: string | null
    name?: string | null
  } | null
}
export type PlanDuplicationRuleRecord = Tables<'compensation_rules'> & {
  compensation_rule_drafts: Tables<'compensation_rule_drafts'> | null
  compensation_rule_versions: Tables<'compensation_rule_versions'>[] | null
}

export type PlanDuplicationRecord = Pick<
  Tables<'plans'>,
  'id' | 'organization_id' | 'label' | 'description' | 'currency' | 'source'
> & {
  teams_plans: Pick<Tables<'teams_plans'>, 'team_id'>[] | null
  compensation_rules: PlanDuplicationRuleRecord[] | null
}

export type PeriodRulesAffectedScopeRow = {
  id?: number
  organization_id: number
  profile_id: string | null
  metric_id: number
  affected_date: string
  credit_setup_id?: number | null
  credit_setup_rule_id?: number | null
  team_id?: number | null
  compensation_rule_id?: number | null
  recalculation_reason?: string | null
  updated_at?: string
}

export type PeriodRulesAffectedScopePaginatedRow = PeriodRulesAffectedScopeRow & {
  id: number
  updated_at: string
}

export type PeriodRulesAffectedScopeCursor = Pick<
  PeriodRulesAffectedScopePaginatedRow,
  'id' | 'updated_at'
>

export type PeriodRulesAffectedScopePage = {
  rows: PeriodRulesAffectedScopePaginatedRow[]
  throughCursor: PeriodRulesAffectedScopeCursor | null
  hasMore: boolean
}

export type PeriodRulesAffectedScopeOrganizationStats = {
  organization_id: number
  affected_count: number
  first_seen: string
  last_seen: string
}

export type GetOrganizationMetricsValuesParams = {
  metricIds: number[]
  startDate: string
  endDate: string
  organizationId: number
  sourceId?: number
  metricsToUpsert?: TablesInsert<'metric_values'>[]
  metricsToExclude?: Pick<
    TablesInsert<'metric_values'>,
    | 'metric_id'
    | 'deal_id'
    | 'activity_id'
    | 'invoice_id'
    | 'kpi_id'
    | 'line_item_id'
    | 'deal_line_item_id'
    | 'custom_record_id'
  >[]
  groupBy?: string
  cumulative?: boolean
  targetCurrency?: string
}

export type CreditSetupRuleForMetric = Pick<
  Tables<'credit_setup_rules'>,
  'id' | 'credit_setup_id' | 'value_filters' | 'archived_at'
> & {
  credit_setups: Pick<Tables<'credit_setups'>, 'id' | 'source_metric_id' | 'archived_at'> & {
    metrics: Pick<Tables<'metrics'>, 'organization_id'>
  }
}

export type CreditSetupRuleMetricValueReconciliation = {
  credit_setup_rule_id: number
  candidate_metric_value_ids: number[]
  matching_metric_value_ids: number[]
}

export type CreditSetupRuleMetricValueScopeRow = {
  attribution_key: string
  credit_setup_id: number
  credit_setup_rule_id: number
  organization_id: number
  metric_value_id: number
  metric_id: number
  profile_id: string | null
  accrual_date: string
}

export type CreditSetupRuleMetricValueBootstrapStatus = {
  source_metric_id: number
  active_rule_count: number
  eligible_edge_count: number
}

export type CreditedMetricValueRequest = {
  organizationId: number
  metricId: number
  creditSetupId: number
  profileId: string | null
  teamIds: number[]
  teamMemberships?: {
    teamId: number
    effectiveFrom: string
    effectiveTo: string | null
  }[]
  valueProfileIds?: string[] | null
  valueTeamIds?: number[]
  sourceIdLimitOnLastDay?: number
  targetCurrency?: string
  callSite?: string
  aggregateFunction?: Enums<'aggregate_function'>
  ruleSegments?: {
    creditSetupRuleId: number
    effectiveFrom: string
    effectiveTo: string
  }[]
  startDate: string
  endDate: string
}

export type CreditedTargetMetricValueAggregate = {
  request_id: number
  value: number
}

export type CreditedMetricValueTelemetry = {
  callSites: string[]
  durationMs: number
  error?: string
  requestCount: number
  resultRowCount: number
  uniqueContextCount: number
}

export type CreditedMetricValueRow = Omit<
  DBFunctionReturns<'get_credited_metric_values'>[number],
  'profile_id'
> & {
  profile_id: string | null
}

export type PeriodRulesRunWatermark = {
  organization_id: number
  last_successful_run_at: string
}

export type DataIssueProcessingStatus = Tables<'data_issue_processing_streams'>['status']

export type DataIssueProcessingRunStatus = Tables<'data_issue_processing_runs'>['status']

export type DataIssueProcessingStream = Tables<'data_issue_processing_streams'>

export type DataIssueProcessingRun = Tables<'data_issue_processing_runs'>

export type DataIssueProcessingStreamSelector = {
  organizationId: Tables<'data_issue_processing_streams'>['organization_id']
  source: Tables<'data_issue_processing_streams'>['source']
  isCustom: Tables<'data_issue_processing_streams'>['is_custom']
}

type ClaimDataIssueProcessingStreamArgs =
  Database['public']['Functions']['claim_data_issue_processing_stream']['Args']

export type DataIssueProcessingClaimArgs = DataIssueProcessingStreamSelector & {
  leaseToken: ClaimDataIssueProcessingStreamArgs['_lease_token']
  leaseDurationSeconds?: ClaimDataIssueProcessingStreamArgs['_lease_duration_seconds']
}

export type DataIssueProcessingClaim = {
  stream: DataIssueProcessingStream
  run: DataIssueProcessingRun
}

export type DataIssueProcessingHeartbeatArgs = {
  streamId: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_stream_id']
  runId: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_run_id']
  leaseToken: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_lease_token']
  leaseDurationSeconds?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_lease_duration_seconds']
  recordsSeen?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_records_seen']
  recordsProcessed?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_records_processed']
  issuesSaved?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_issues_saved']
  issuesRemoved?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_issues_removed']
  lastProcessedUpdatedAt?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_last_processed_updated_at']
  lastProcessedId?: Database['public']['Functions']['heartbeat_data_issue_processing_run']['Args']['_last_processed_id']
}

export type DataIssueProcessingFinalizeArgs = {
  streamId: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_stream_id']
  runId: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_run_id']
  leaseToken: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_lease_token']
  status: Exclude<DataIssueProcessingRunStatus, 'running'>
  failureReason?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_failure_reason']
  cursorUpdatedAt?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_cursor_updated_at']
  cursorId?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_cursor_id']
  snapshotUpperUpdatedAt?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_snapshot_upper_updated_at']
  snapshotUpperId?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_snapshot_upper_id']
  recordsSeen?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_records_seen']
  recordsProcessed?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_records_processed']
  issuesSaved?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_issues_saved']
  issuesRemoved?: Database['public']['Functions']['finalize_data_issue_processing_run']['Args']['_issues_removed']
}

export type PendingToReviewItemsCursor = {
  id: Tables<'deals'>['id']
  updatedAt: Tables<'deals'>['updated_at']
}

export type PendingToReviewItemsOptions = {
  after?: PendingToReviewItemsCursor | null
  limit?: number
  until?: PendingToReviewItemsCursor | null
}

export type NotificationReferenceProfile = Pick<
  Tables<'profiles'>,
  'id' | 'name' | 'email' | 'img_url'
>

export type NotificationReferenceDeal = Pick<Tables<'deals'>, 'deal_id' | 'name'>

export type NotificationReferenceInvoice = Pick<Tables<'invoices'>, 'invoice_number'> & {
  deals: NotificationReferenceDeal | null
}

export type NotificationReferenceActivity = Pick<Tables<'activities'>, 'activity_id' | 'type'> & {
  deals: NotificationReferenceDeal | null
}

export type NotificationReferenceKpi = Pick<Tables<'kpis'>, 'id'> & {
  kpis_tables: Pick<Tables<'kpis_tables'>, 'label'> | null
}

export type NotificationReferenceLineItem = Pick<Tables<'line_items'>, 'id'> & {
  invoices: NotificationReferenceInvoice | null
}

export type NotificationReferenceDealLineItem = Pick<
  Tables<'deal_line_items'>,
  'id' | 'line_item_id' | 'product_name' | 'description'
> & { deals: NotificationReferenceDeal | null }

export type TargetPeriodDatesRow = DBFunctionReturns<'get_target_period_dates'>[number]

export type GetUsersRankingForTargetParams = {
  endDate: string
  order?: string | null
  organizationId: number
  startDate: string
  tags?: number[] | null
  targetId: number
  teamId?: number | null
}

export type UsersRankingForTargetRow = DBFunctionReturns<'get_users_ranking_for_target'>[number]

export type TargetCumulativeAchievementRow =
  DBFunctionReturns<'get_period_target_achievement'>[number]

export type CreditedTargetAchievementSeriesParams = {
  targetId: number
  profileId: string
  startDate: string
  endDate: string
  groupBy: string
  ruleSegments: {
    creditSetupRuleId: number
    effectiveFrom: string
    effectiveTo: string
  }[]
}

export type NotificationPayoutReference = Pick<
  Tables<'payouts'>,
  'id' | 'description' | 'effective_date' | 'compensation'
> & {
  deals: NotificationReferenceDeal | null
  invoices: NotificationReferenceInvoice | null
  activities?: NotificationReferenceActivity | null
  kpis?: NotificationReferenceKpi | null
  line_items?: NotificationReferenceLineItem | null
  deal_line_items?: NotificationReferenceDealLineItem | null
  profiles: NotificationReferenceProfile | null
  compensation_rules: Pick<Tables<'compensation_rules'>, 'label' | 'source'> | null
}

export type PayoutManualAdjustmentStep = {
  from_value: number | null
  to_value: number
  adjustment_value: number | null
  created_at: string
  profile: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'> | null
}

export type PayoutManualAdjustmentBreakdown = {
  payout_id: number
  original_value: number | null
  adjusted_value: number
  adjustment_value: number | null
  created_at: string
  profile: Pick<Tables<'profiles'>, 'id' | 'name' | 'email' | 'img_url'> | null
  adjustments: PayoutManualAdjustmentStep[]
}

export type NotificationStatementReference = Pick<
  Tables<'statements'>,
  'id' | 'period_start' | 'period_end' | 'profile_id' | 'organization_id'
> & {
  status?: Tables<'statements'>['status'] | null
  profiles: NotificationReferenceProfile | null
}

export type StatementPeriodReference = Pick<Tables<'statements'>, 'id'> & {
  period_start: string | null
}
export type DataView = Tables<'data_views'>
export type DataViewInsert = TablesInsert<'data_views'>
export type DataViewUpdate = TablesUpdate<'data_views'>

export type DataViewListParams = {
  organizationId: number
  includeArchived?: boolean
}

export type DataViewCreateParams = Omit<DataViewInsert, 'organization_id' | 'created_by'> & {
  organizationId: number
  createdBy: string
}

export type DataViewUpdateParams = {
  id: number
  organizationId: number
  updates: DataViewUpdate
}

export type DataViewFilterField = {
  name: string
  type: string
}

export type DataViewFilterFieldsParams = {
  organizationId: number
  source: Enums<'plan_source'>
  virtualTableId?: number
}
export type DataTableCreationResult = {
  tableId: number
}

export type CreateKpiTableWithTeamsParams = {
  label: string
  period: Tables<'kpis_tables'>['period']
  organizationId: number
  teamIds: number[]
}

export type CreateCustomRecordTableWithTeamsParams = {
  label: string
  organizationId: number
  teamIds: number[]
}
export type UpdateDataTableTeamsParams = {
  tableId: number
  organizationId: number
  teamIds: number[]
}

export type CompensationRuleAuthoringReferences = {
  plan: Pick<Tables<'plans'>, 'id' | 'label' | 'organization_id' | 'source'> | null
  rule:
    | (Pick<
        Tables<'compensation_rules'>,
        | 'id'
        | 'plan_id'
        | 'label'
        | 'description'
        | 'source'
        | 'diagram'
        | 'type'
        | 'outcome_period'
        | 'user_field'
        | 'accrual_date_field'
        | 'kpis_table_id'
        | 'custom_record_table_id'
      > & {
        compensation_rule_drafts: Pick<
          Tables<'compensation_rule_drafts'>,
          'diagram' | 'is_tvp_based'
        > | null
      })
    | null
  fields: Array<{
    name: string
    label?: string
    type?: string
    organization_id?: number
  }>
  metrics: Array<
    Pick<
      Tables<'metrics'>,
      | 'id'
      | 'label'
      | 'type'
      | 'source'
      | 'kpis_table_id'
      | 'custom_record_table_id'
      | 'organization_id'
    >
  >
  targets: Array<
    Pick<Tables<'targets'>, 'id' | 'label' | 'metric_id' | 'type' | 'period' | 'organization_id'>
  >
}

export type CompensationRuleAuthoringReferenceSearchResult = {
  fields: Array<{ name: string; label?: string; type?: string }>
  metrics: Array<
    Pick<
      Tables<'metrics'>,
      | 'id'
      | 'label'
      | 'description'
      | 'source'
      | 'type'
      | 'kpis_table_id'
      | 'custom_record_table_id'
    >
  >
  targets: Array<Pick<Tables<'targets'>, 'id' | 'label' | 'metric_id' | 'type' | 'period'>>
}

export type CompensationRuleAuthoringSessionRpcParams = {
  organizationId: number
  planId: number
  compensationRuleId?: number
  ruleMetadata: Json
  diagram: Json
  contextFingerprint?: string
  ttlSeconds?: number
}

export type CompensationRuleAuthoringSessionUpdateRpcParams = {
  sessionId: string
  expectedRevision: number
  diagram: Json
  ruleMetadata: Json
  contextFingerprint?: string
  modelMetadata?: Json
  referencedFields?: string[]
  referencedMetricIds?: number[]
  referencedTargetIds?: number[]
}

export type CompensationRuleAuthoringCommitRpcParams = {
  sessionId: string
  expectedRevision: number
  operationKey: string
  draftHash: string
  contextFingerprint: string
}
export type CompensationRuleDraftHistoryEvent = {
  beforeState: Json
  afterState: Json
  beforeHash: string
  afterHash: string
  operationKind: 'edit' | 'undo' | 'redo'
}

export type CompensationRuleDraftHistoryRow = {
  id: string
  organization_id: number
  compensation_rule_id: number
  actor_id: string
  before_state: Json
  after_state: Json
  before_hash: string
  after_hash: string
  operation_kind: 'edit' | 'undo' | 'redo'
  created_at: string
  history_sequence: number
}
export type CompensationRuleDraftHistoryRecord = Pick<
  CompensationRuleDraftHistoryRow,
  | 'id'
  | 'organization_id'
  | 'compensation_rule_id'
  | 'actor_id'
  | 'operation_kind'
  | 'created_at'
  | 'history_sequence'
>

export abstract class DolfinRpc {
  abstract getManualCompensationImportContext(
    organizationId: number,
    actorId: string
  ): Promise<CompensationImportContext>

  abstract getManualCompensationImport(
    organizationId: number,
    actorId: string,
    hash: string
  ): Promise<Json | null>

  abstract commitManualCompensationImport(
    organizationId: number,
    actorId: string,
    hash: string,
    rows: Json
  ): Promise<Json>

  abstract isOrganizationMember(profileId: string, organizationId: number): Promise<boolean>

  abstract isOrganizationActiveMember(profileId: string, organizationId: number): Promise<boolean>

  abstract isActiveOrganizationAdmin(profileId: string, organizationId: number): Promise<boolean>

  abstract isActiveOrganizationMember(profileId: string, organizationId: number): Promise<boolean>

  abstract getOrganizationProfileIdByEmail(
    organizationId: number,
    email: string
  ): Promise<string | null>

  abstract getOrganizationProfileEmailById(
    organizationId: number,
    profileId: string
  ): Promise<string | null>

  abstract getDealIdByExternalId(organizationId: number, externalId: string): Promise<number | null>

  abstract getLineItemByExternalId(
    organizationId: number,
    externalId: string
  ): Promise<{ id: number; invoice_id: number | null } | null>

  abstract getDealLineItemByExternalId(
    organizationId: number,
    externalId: string
  ): Promise<{ id: number; deal_id: number } | null>

  abstract dataRecordExists(
    resource: DataIngestionResource,
    organizationId: number,
    externalId: string
  ): Promise<boolean>

  abstract ingestDataRecord(
    resource: DataIngestionResource,
    organizationId: number,
    record: Json
  ): Promise<DataIngestionRpcResult>

  abstract getManagedIngestionBatchContext(params: {
    organizationId: number
    resource: DataIngestionResource
    externalIds: string[]
    ownerEmails: string[]
    profileIds: string[]
    dealExternalIds: string[]
    lineItemExternalIds: string[]
  }): Promise<DataIngestionBatchContext>

  abstract ingestManagedDataRecords(
    resource: DataIngestionResource,
    organizationId: number,
    records: Json[]
  ): Promise<DataIngestionBatchRpcResult[]>

  abstract getDisputeTags(organizationId: number): Promise<DisputeTag[]>

  abstract getDisputeThread(params: GetDisputeThreadParams): Promise<NotificationsQuery | null>
  abstract getDisputeImageComment(params: {
    organizationId: number
    issueId: number
    commentId: number
  }): Promise<{ image_path: string | null; profile_id: string } | null>
  abstract validateDisputeImage(params: {
    organizationId: number
    issueId: number
    imagePath: string
  }): Promise<boolean>
  abstract createDisputeComment(params: {
    notificationId: number
    currentUserId: string
    content: string
    imagePath?: string | null
  }): Promise<NotificationsQuery['comments'][number]>

  abstract updateDisputeStatus(params: {
    organizationId: number
    issueId: number
    status: DisputeAnalyticsStatus
    actorId: string
  }): Promise<void>

  abstract createDisputeTag(
    organizationId: number,
    issueId: number,
    tag: string
  ): Promise<DisputeTag>

  abstract getDisputeIssueTags(organizationId: number, issueId: number): Promise<DisputeTag[]>

  abstract setDisputeIssueTags(
    organizationId: number,
    issueId: number,
    tagIds: number[]
  ): Promise<void>

  abstract getDisputeAnalyticsIssues(params: {
    organizationId: number
    start: string
    end: string
    statuses?: DisputeAnalyticsStatus[]
  }): Promise<DisputeAnalyticsIssueRow[]>

  abstract getDisputeAnalyticsTeams(params: {
    organizationId: number
    lookups: DisputeAnalyticsTeamLookup[]
  }): Promise<Map<number, DisputeAnalyticsTeamReference>>

  abstract getTeamMembershipsInPeriod(params: {
    organizationId: number
    start: string
    end: string
    teamIds?: number[]
    profileId?: string
  }): Promise<TeamMembershipInPeriod[]>

  abstract getProfileOrganizationActivePeriods(params: {
    organizationId: number
    profileId: string
    start: string
    end: string
  }): Promise<ProfileOrganizationActivePeriod[]>

  abstract getManagerId(profileId: string, organizationId: number): Promise<string | undefined>

  abstract getTeam(
    profileId: string,
    organizationId: number,
    date: string,
    options?: { throwOnError?: boolean }
  ): Promise<Tables<'teams'> | null>

  abstract loadChecksConfig(
    organizationId: number
  ): Promise<Tables<'organizations'>['data_issues_checks_config']>

  abstract getFixedCurrencyRate(
    from: string,
    to: string,
    date: string,
    organizationId: number
  ): Promise<number | null>

  abstract getMarketCurrencyRate(from: string, to: string, date: string): Promise<number | null>

  abstract getPeriodTargetAchievement(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    effectiveDate?: string,
    currentEntityId?: number
  ): Promise<{
    achievement: number
    period: string | null
    target_amount: number
    period_start: string
    period_end: string
    metric_source: Enums<'plan_source'> | null
    metric_formula: string
    global_target: boolean
    team_target: boolean
    team_ids: number[]
  } | null>

  abstract getPeriodTargetAchievementWithEvidence(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    effectiveDate?: string,
    currentEntityId?: number
  ): Promise<
    | ({
        achievement: number
        period: string | null
        target_amount: number
        period_start: string
        period_end: string
        metric_source: Enums<'plan_source'> | null
        metric_formula: string
        global_target: boolean
        team_target: boolean
        team_ids: number[]
      } & { creditedEvidence: CreditedTargetEvidence[] })
    | null
  >

  abstract getPeriodTargetAmount(
    targetId: number,
    start: string,
    end: string,
    profileId: string
  ): Promise<DBFunctionReturns<'get_period_target_amount'>[0]>

  abstract getTargetPeriodDates(
    referenceDate: string,
    targetPeriod: Enums<'period'>
  ): Promise<TargetPeriodDatesRow[]>

  abstract getUsersRankingForTarget(
    args: GetUsersRankingForTargetParams
  ): Promise<UsersRankingForTargetRow[]>

  abstract getTargetCreditSetupSegments(params: {
    targetId: number
    profileIds: string[]
    startDate: string
    endDate: string
  }): Promise<TargetCreditSetupSegment[]>

  abstract getUserTargetsAchievementInPeriod(
    profileId: string,
    start: string,
    end: string,
    organizationId: number,
    targetId?: number,
    onlyUserAchievementCounts?: boolean
  ): Promise<TargetAchievementRow[] | null>

  abstract getUserViewTargetsSummary(
    profileId: string,
    start: string,
    end: string,
    organizationId: number
  ): Promise<TargetAchievementRow[] | null>

  abstract getOrganizationCompletedTargetAttainment(
    organizationId: number,
    startDate: string,
    endDate: string,
    targetId?: number
  ): Promise<DBFunctionReturns<'get_organization_completed_target_attainment'>>

  abstract getOrgCurrencyExchangeConfig(
    organizationId: number
  ): Promise<Pick<
    Tables<'organizations'>,
    'use_fixed_exchange_rates' | 'use_invoices_exchange_rates'
  > | null>

  abstract getExchangeConfigs(
    organizationId: number,
    tableName: Enums<'customizable_tables'>
  ): Promise<Tables<'exchange_config'>[]>

  abstract getCumulativePeriodTargetAchievement(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    groupBy: string
  ): Promise<TargetCumulativeAchievementRow[]>

  abstract getCreditedTargetAchievementSeries(
    params: CreditedTargetAchievementSeriesParams
  ): Promise<TargetCumulativeAchievementRow[]>

  abstract getRecordHistory(
    recordId: number,
    source: Enums<'plan_source'>,
    startDate: string,
    endDate: string
  ): Promise<{ changed_at: string; record: RecordData }[]>

  abstract getPeriodTargetOutcomes(
    targetId: number,
    start: string,
    end: string,
    profileId: string,
    planId?: number
  ): Promise<{ profile_id: string; outcome: number }[]>

  abstract getMetricsValues(
    metricIds: number[],
    periodStart: string,
    endDate: string,
    organizationId: number,
    profileIds?: string[],
    teamIds?: number[],
    sourceId?: number,
    metricsToUpsert?: TablesInsert<'metric_values'>[],
    metricsToExclude?: Pick<
      TablesInsert<'metric_values'>,
      | 'metric_id'
      | 'deal_id'
      | 'activity_id'
      | 'invoice_id'
      | 'kpi_id'
      | 'line_item_id'
      | 'deal_line_item_id'
      | 'custom_record_id'
    >[],
    groupBy?: string,
    cumulative?: boolean,
    targetCurrency?: string
  ): Promise<DBFunctionReturns<'get_metrics_values'>>

  abstract getTargetContributingRecords(
    params: TargetContributingRecordsParams
  ): Promise<TargetContributingRecordsPage>

  abstract getTargetContributingRecordsAccess(
    params: TargetContributingRecordsAccessParams
  ): Promise<TargetContributingRecordsAccess>

  abstract getTargetTeamIds(targetId: number, organizationId: number): Promise<number[]>

  abstract getMetricsByIdsForPeriod(
    metricIds: number[],
    organizationId: number,
    periodStart: string
  ): Promise<MetricReference[]>

  abstract getOrganizationProfileIds(organizationId: number): Promise<string[]>

  /** Read-only, organization-scoped inputs for on-demand Metric calculation. */
  abstract getMetricPreviewRecipientsPage(params: {
    organizationId: number
    profileIds?: string[]
    search?: string
    offset: number
    limit: number
    sortDescending?: boolean
  }): Promise<MetricPreviewRecipientPage>

  abstract getMetricPreviewSourceCandidates(params: {
    metric: Tables<'metrics'>
    profileIds: string[]
    periodStart: string
    periodEnd: string
    limit: number
    customFields?: Record<string, Tables<'custom_fields'>>
    includedOnly?: boolean
    onPage?: (records: RecordData[]) => Promise<void>
  }): Promise<RecordData[]>

  abstract getMetricPreviewDealCustomDateCandidateIds(params: {
    organizationId: number
    customFieldId: number
    profileIds: string[]
    dateStart: string
    dateEnd: string
    limit: number
    ownerCustomFieldId?: number
  }): Promise<number[]>

  abstract getOrganizationMetricsValues(
    params: GetOrganizationMetricsValuesParams
  ): Promise<DBFunctionReturns<'get_organization_metrics_values'>>

  abstract getPendingProcessingCountForMetric(
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    teamIds?: number[]
  ): Promise<number | undefined>

  abstract getOrganizationPendingProcessingCountForMetric(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number
  ): Promise<number | undefined>

  abstract getMetricSourceRecords(
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    teamIds?: number[],
    targetCurrency?: string
  ): Promise<MetricSourceRecord[]>

  abstract getTargetMetricSourceRecords(
    targetId: number,
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    targetCurrency?: string
  ): Promise<MetricSourceRecord[]>

  abstract getTargetMetricSourceRecordsPage(
    targetId: number,
    metricId: number,
    profileId: string,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    params: TargetContributingSourceRecordsPageParams,
    targetCurrency?: string
  ): Promise<MetricSourceRecordsPage>

  abstract getOrganizationMetricSourceRecords(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    targetCurrency?: string
  ): Promise<MetricSourceRecord[]>

  abstract getOrganizationMetricSourceRecordsPage(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    profileId: string | undefined,
    params: TargetContributingSourceRecordsPageParams,
    targetCurrency?: string
  ): Promise<MetricSourceRecordsPage>

  abstract getMetricTargetScope(
    targetId: number,
    profileId: string,
    organizationId: number,
    periodStart: string,
    periodEnd: string
  ): Promise<MetricTargetScopeAccess | null>

  abstract getCreditSetupRuleMetricSourceRecordsPage(
    metricId: number,
    periodStart: string,
    periodEnd: string,
    organizationId: number,
    profileId: string | undefined,
    params: TargetContributingSourceRecordsPageParams,
    targetCurrency?: string
  ): Promise<MetricSourceRecordsPage>

  abstract getMetricsByPeriod(params: {
    aggregateFunction?: Enums<'aggregate_function'>
    endDate: string
    metricIds?: number[]
    organizationId: number
    period?: 'day' | 'week' | 'month'
    startDate: string
    tagIds?: number[]
    teamIds?: number[]
    userIds?: string[]
  }): Promise<DBFunctionReturns<'get_metrics_by_period'>>

  // Compensation history always comes from the RPC so every caller shares the
  // same historical team/business-unit resolution and team-tag bucketing rules.
  abstract getCompensationHistoryReport(params: {
    compensationRuleIds?: number[]
    groupBy:
      | 'business_unit'
      | 'compensation_rule'
      | 'compensation_status'
      | 'plan'
      | 'team'
      | 'team_tag'
      | 'compensation_type'
    groupTagIds?: number[]
    organizationId: number
    planId?: number
    tags?: number[]
    teamId?: number
    year: number
  }): Promise<DBFunctionReturns<'get_compensation_history_report'>>

  abstract getCompensationHistoryProfileReport(params: {
    compensationRuleIds?: number[]
    organizationId: number
    planId?: number
    tags?: number[]
    teamId: number
    year: number
  }): Promise<DBFunctionReturns<'get_compensation_history_profile_report'>>

  // Options are returned by a focused RPC so the selector can reflect the
  // current historical team, tag, and plan scope without a UI-side query.
  abstract getCompensationHistoryCompensationRules(params: {
    organizationId: number
    planId?: number
    tags?: number[]
    teamId?: number
    year: number
  }): Promise<DBFunctionReturns<'get_compensation_history_compensation_rules'>>

  abstract getCompensationHistoryMetricBuckets(params: {
    groupBy: 'profile' | 'team'
    metricId: number
    organizationId: number
    tags?: number[]
    teamId?: number
    year: number
  }): Promise<DBFunctionReturns<'get_compensation_history_metric_buckets'>>
  abstract searchCompensationRecords(
    params: CompensationRecordSearchParams
  ): Promise<CompensationRecordSearchMatch[]>

  abstract saveCurrencyRate(
    from: string,
    to: string,
    rate: number,
    daterange: string
  ): Promise<Error | void>

  abstract getInvoicesDealLinkField(organizationId: number): Promise<string | undefined | null>

  abstract getOrgCurrency(
    organizationId: number
  ): Promise<Pick<Tables<'organizations'>, 'currency'>>

  abstract searchOrganizationProfiles(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationProfileSearchResult[]>

  abstract searchOrganizationTeams(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationTeamSearchResult[]>
  abstract getAdminOrganizationMrrOverview(): Promise<AdminOrganizationMrrOverviewRow[]>
  abstract getActiveOrganizationUserCount(organizationId: number): Promise<number>

  abstract saveAdminOrganizationMrrSetting(
    params: AdminOrganizationMrrSettingInput
  ): Promise<AdminOrganizationMrrSetting>

  abstract searchCreditSetupRuleAssignmentSubjects(params: {
    organizationId: number
    subjectType: 'profile' | 'team'
    query: string
    offset: number
    limit: number
  }): Promise<CreditSetupRuleAssignmentSubjectPage>

  abstract searchOrganizationPlans(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationPlanSearchResult[]>

  abstract searchOrganizationTargets(params: {
    organizationId: number
    query: string
    limit: number
  }): Promise<OrganizationTargetSearchResult[]>

  abstract searchOrganizations(params: {
    organizationIds: number[]
    query: string
    limit: number
  }): Promise<OrganizationSearchResult[]>

  abstract getOrganizationsByIds(organizationIds: number[]): Promise<OrganizationReference[]>

  abstract getOrganizationProfilesData(
    organizationId: number,
    profileIds: string[]
  ): Promise<Record<string, Tables<'organizations_profiles_data'>[]>>

  abstract upsertOrganizationProfileDataSnapshot(params: {
    organizationId: number
    profileId: string
    fromDate: string
    type?: string | null
    totalVariablePay?: number | null
  }): Promise<Tables<'organizations_profiles_data'>>

  abstract getActiveOrganizationProfilesData(
    organizationId: number,
    referenceDate: string,
    profileIds?: string[]
  ): Promise<CurrentOrganizationProfileData[]>

  // Returns the org-scoped business-unit hierarchy already enriched with the
  // reusable entities needed by the UI: cost centers, departments, legal
  // entities, locations, and assigned-user counts.
  abstract getBusinessUnits(
    organizationId: number,
    includeArchived?: boolean
  ): Promise<BusinessUnitListItem[]>

  abstract getProviderProfileIdentity(
    providerProfileId: string,
    providerSpaceId: string
  ): Promise<ProviderProfileIdentity | null>

  abstract getAdminProviderProfileIdentity(
    providerProfileId: string,
    providerSpaceId: string
  ): Promise<ProviderProfileIdentity | null>

  abstract getOrganizationProfilesTeamEvents(
    organizationId: number,
    profileIds: string[]
  ): Promise<Record<string, TeamMembershipEvent[]> | null>

  abstract listImpersonationCandidateProfiles(
    args: ListImpersonationCandidateProfilesParams
  ): Promise<ImpersonationCandidateProfile[]>

  abstract getImpersonationProfile(profileId: string): Promise<ImpersonationProfile | null>

  abstract createImpersonationAttempt(
    args: CreateImpersonationAttemptParams
  ): Promise<CreateImpersonationAttemptResult>

  abstract markImpersonationLinkGenerationFailed(
    auditLogId: string,
    errorMessage: string
  ): Promise<void>

  abstract markImpersonationLinkGenerated(auditLogId: string): Promise<void>

  abstract getImpersonationAuditLog(auditLogId: string): Promise<ImpersonationAuditLog | null>

  abstract closeImpersonation(
    auditLogId: CloseImpersonationParams['auditLogId'],
    endedAt: CloseImpersonationParams['endedAt'],
    errorMessage?: CloseImpersonationParams['errorMessage'],
    options?: {
      expectedCurrentStatus?: Tables<'impersonation_logs'>['status']
      requireUnended?: boolean
      values?: Omit<TablesUpdate<'impersonation_logs'>, 'ended_at' | 'status' | 'error_message'>
    }
  ): Promise<void>

  abstract getUserCompensationRules(
    profileId: string,
    organizationId: number,
    startDate: string,
    endDate: string
  ): Promise<DBFunctionReturns<'get_user_compensation_rules'>>

  abstract getUserMetricsFromCompensationRule(
    ruleId: number,
    periodStart: string,
    periodEnd: string,
    profileId: string,
    organizationId: number
  ): Promise<DBFunctionReturns<'get_user_metrics_from_compensation_rules'>>

  abstract getCompensationRuleVersionMetricIds(ruleVersionIds: number[]): Promise<number[]>

  abstract getPendingProcessingItems(
    source: Enums<'plan_source'>,
    organizationId?: number
  ): Promise<RecordData[]>

  abstract upsertPeriodRulesAffectedScope(rows: PeriodRulesAffectedScopeRow[]): Promise<number>

  abstract getPeriodRulesAffectedScopeSnapshot(): Promise<string>

  abstract listPeriodRulesAffectedScopeOrganizations(
    snapshotBefore: string
  ): Promise<PeriodRulesAffectedScopeOrganizationStats[]>

  abstract getPeriodRulesAffectedScopePage(
    organizationId: number,
    snapshotBefore: string,
    afterCursor?: PeriodRulesAffectedScopeCursor | null
  ): Promise<PeriodRulesAffectedScopePage>

  abstract getPeriodRulesAffectedScope(
    organizationId: number,
    snapshotBefore: string
  ): Promise<PeriodRulesAffectedScopePaginatedRow[]>

  abstract ackPeriodRulesAffectedScopeChunk(
    organizationId: number,
    snapshotBefore: string,
    rowIds: number[]
  ): Promise<number>

  abstract listPeriodRulesRunWatermarks(
    organizationIds: number[]
  ): Promise<PeriodRulesRunWatermark[]>

  abstract upsertPeriodRulesRunWatermark(
    organizationId: number,
    ranAt?: string
  ): Promise<string | null>

  abstract ensureDataIssueProcessingStreams(
    organizationId: number
  ): Promise<DataIssueProcessingStream[]>

  abstract getDataIssueProcessingStream(
    selector: DataIssueProcessingStreamSelector
  ): Promise<DataIssueProcessingStream | null>

  abstract claimDataIssueProcessingStream(
    args: DataIssueProcessingClaimArgs
  ): Promise<DataIssueProcessingClaim | null>

  abstract heartbeatDataIssueProcessingRun(args: DataIssueProcessingHeartbeatArgs): Promise<boolean>

  abstract finalizeDataIssueProcessingRun(
    args: DataIssueProcessingFinalizeArgs
  ): Promise<DataIssueProcessingRun | null>

  abstract getPendingToReviewItemsUpperBound(
    source: Extract<Enums<'plan_source'>, 'deal' | 'activity' | 'invoice'>,
    organizationId: number
  ): Promise<PendingToReviewItemsCursor | null>

  abstract getPendingToReviewItems(
    source: 'deal',
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<(Tables<'deals'> & { invoices: Tables<'invoices'>[] | null })[]>
  abstract getPendingToReviewItems(
    source: 'activity',
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<ActivitiesWithDeals[]>
  abstract getPendingToReviewItems(
    source: 'invoice',
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<InvoicesWithDeals[]>
  abstract getPendingToReviewItems(
    source: Extract<Enums<'plan_source'>, 'deal' | 'activity' | 'invoice'>,
    organizationId: number,
    options?: PendingToReviewItemsOptions
  ): Promise<
    | (Tables<'deals'> & { invoices: Tables<'invoices'>[] | null })[]
    | ActivitiesWithDeals[]
    | InvoicesWithDeals[]
  >
  abstract createKpiTableWithTeams(
    params: CreateKpiTableWithTeamsParams
  ): Promise<DataTableCreationResult>

  abstract createCustomRecordTableWithTeams(
    params: CreateCustomRecordTableWithTeamsParams
  ): Promise<DataTableCreationResult>

  abstract updateKpiTableTeams(params: UpdateDataTableTeamsParams): Promise<void>

  abstract updateCustomRecordTableTeams(params: UpdateDataTableTeamsParams): Promise<void>
  abstract listDataViews(params: DataViewListParams): Promise<DataView[]>

  abstract getDataViewFilterFields(
    params: DataViewFilterFieldsParams
  ): Promise<DataViewFilterField[]>

  abstract createDataView(params: DataViewCreateParams): Promise<DataView>

  abstract updateDataView(params: DataViewUpdateParams): Promise<DataView>

  abstract archiveDataView(params: { id: number; organizationId: number }): Promise<void>

  abstract getRecordCompensationRules(
    organizationId: number,
    source: Enums<'plan_source'>,
    sourceTableId?: number
  ): Promise<
    (Tables<'compensation_rules'> & {
      plans: { label?: string; teams_plans_history: PlanTeamHistoryLink[] } | null
      compensation_rule_versions: Tables<'compensation_rule_versions'>[]
    })[]
  >

  abstract getCompensationRulesForExplanation(
    organizationId: number
  ): Promise<CompensationRuleForExplanation[]>

  abstract findCompensationRuleByIdForOrg(
    compensationRuleId: number,
    organizationId?: number
  ): Promise<
    | (Tables<'compensation_rules'> & {
        plans?: { organization_id: number } | null
      })
    | null
  >

  abstract transitionCompensationRuleVersion(
    params: CompensationRuleVersionTransition
  ): Promise<Json>

  abstract overrideCompensationRuleVersion(params: {
    compensationRuleId: number
    versionId: number
    diagram: Json
    isTvpBased: boolean
  }): Promise<Pick<Tables<'compensation_rule_versions'>, 'id'> | null>

  abstract getOrganizationTeams(organizationId: number): Promise<Tables<'teams'>[]>

  abstract getTeamsByIds(
    organizationId: number,
    teamIds: number[]
  ): Promise<Pick<Tables<'teams'>, 'id' | 'name' | 'currency'>[]>

  abstract getOrganizationTeamsForCompRule(
    organizationId: number,
    compensationRuleId: number,
    includeHistoricalPlanLinks?: boolean
  ): Promise<Array<Pick<Tables<'teams'>, 'id' | 'name'>>>

  abstract getProfilesForTeamIds(teamIds: number[]): Promise<TeamProfileRow[]>

  abstract saveDiscrepancies(
    data_issues: DataIssuesWithChildrenInsert[],
    source: Enums<'plan_source'>
  ): Promise<Error | void>

  abstract removeDiscrepancies(
    ids: (
      | {
          deal_id: number
          error_code: string
          column_key: string
        }
      | {
          invoice_id: number
          error_code: string
          column_key: string
        }
    )[],
    source: Enums<'plan_source'>,
    organizationId: number
  ): Promise<Error | void>

  abstract saveExecution(execution: TablesInsert<'executions'>): Promise<Error | void>

  abstract getPeriodCompensationRules(organizationId: number): Promise<
    (Tables<'compensation_rules'> & {
      plans: { teams_plans_history: PlanTeamHistoryLink[] } | null
      compensation_rule_versions: (Tables<'compensation_rule_versions'> & {
        compensation_rule_version_metrics: Tables<'compensation_rule_version_metrics'>[]
      })[]
    })[]
  >

  abstract getCustomFields(
    source: Enums<'plan_source'>,
    organizationId: number,
    sourceTableId?: number
  ): Promise<Tables<'custom_fields'>[]>

  abstract backfillConnectedAppUserMappings(params: {
    organizationId: number
    provider: 'hubspot' | 'salesforce'
    mappingDelta: Record<string, string>
  }): Promise<ConnectedAppUserMappingBackfillResult>

  abstract getCustomFieldFilterCandidateIds(
    params: CustomFieldFilterCandidateIdsParams
  ): Promise<number[]>

  abstract getRelationalCustomFieldQueryIds(
    params: RelationalCustomFieldQueryIdsParams
  ): Promise<RelationalCustomFieldQueryIdsResult>

  abstract getDealsGrid(params: GetDealsGridParams): Promise<DealGridResult>

  abstract getActivitiesGrid(params: ActivityGridRequest): Promise<SourceGridResult>

  abstract getInvoicesGrid(params: InvoiceGridRequest): Promise<SourceGridResult>

  abstract getKpisGrid(params: KpiGridRequest): Promise<SourceGridResult>

  abstract getCustomRecordsGrid(params: CustomRecordGridRequest): Promise<SourceGridResult>

  abstract getKpiCustomFields(tableId: number): Promise<Tables<'custom_fields'>[]>

  abstract getKpiTable(
    tableId: number
  ): Promise<Pick<Tables<'kpis_tables'>, 'id' | 'organization_id' | 'label' | 'period'>>

  abstract getKpiPreviewMonths(
    organizationId: number,
    profileIds: string[],
    kpisTableId: number
  ): Promise<Record<string, string[]>>

  abstract recordCalculationFailure(
    source: Enums<'plan_source'>,
    organizationId: number,
    recordIds: number[],
    reason: string
  ): Promise<void>

  abstract saveResults(
    results: ParsedExecutionResult[],
    source?:
      | 'deals'
      | 'activities'
      | 'invoices'
      | 'kpis'
      | 'line_items'
      | 'deal_line_items'
      | 'custom_records'
  ): Promise<Error | void>
  abstract saveCompensationTierSnapshots(
    snapshots: CompensationTierSnapshotWrite[]
  ): Promise<CompensationTierEvent[]>

  abstract claimDomainEventDeliveries(params: {
    consumer: string
    eventType: string
    limit?: number
  }): Promise<DomainEventDelivery[]>

  abstract completeDomainEventDelivery(params: CompleteDomainEventDeliveryParams): Promise<void>

  abstract getPayoutCalculationContext(
    payoutId: number
  ): Promise<PayoutCalculationContextResponse | null>

  abstract movePayoutInstallmentToStatement(args: {
    new_statement_date: string
    payout_installment_id: number
  }): Promise<void>

  abstract getOwnerEmail(ownerId: string): Promise<string | undefined>

  abstract hasFrozenPayouts(
    compRule: Tables<'compensation_rules'>,
    item: RecordData
  ): Promise<boolean>

  abstract hasPeriodFrozenPayouts(
    compRule: Tables<'compensation_rules'>,
    profileId: string,
    period: { start: string; end: string }
  ): Promise<boolean>

  abstract getProfiles(range?: GetProfilesParams): Promise<ProfilesResult>

  abstract getProfileForEdit(
    organizationId: number,
    profileId: string
  ): Promise<ProfileWithJoins | null>

  abstract getMetrics(
    source: Enums<'plan_source'>,
    sourceTableId: number | undefined,
    organizationId: number
  ): Promise<Tables<'metrics'>[]>

  abstract getMetricById(
    metricId: number,
    organizationId?: number
  ): Promise<Tables<'metrics'> | null>

  abstract getNonArchivedMetrics(
    organizationId: number,
    options?: {
      mode?: 'not_archived' | 'not_archived_yet'
      referenceDate?: string
    }
  ): Promise<Tables<'metrics'>[]>

  abstract getFormulaMetrics(organizationId: number): Promise<Tables<'metrics'>[]>

  abstract getActiveCreditSetupRulesForMetric(
    sourceMetricId: number,
    organizationId: number
  ): Promise<CreditSetupRuleForMetric[]>

  abstract getActiveCreditSetupRulesForMetrics(
    sourceMetricIds: number[],
    organizationId: number
  ): Promise<CreditSetupRuleForMetric[]>

  /** Returns source Metrics with any current or archived Credit Setup. */
  abstract getCreditSetupSourceMetricIdsWithHistory(
    sourceMetricIds: number[],
    organizationId: number
  ): Promise<number[]>

  abstract getCreditSetupRuleMetricValueBootstrapStatus(
    organizationId: number,
    sourceMetricIds: number[]
  ): Promise<CreditSetupRuleMetricValueBootstrapStatus[]>

  abstract enqueueMetricRecordsPendingProcessing(metricId: number): Promise<void>

  abstract getCreditSetupRuleMetricValueScope(
    organizationId: number,
    sourceMetricId: number,
    attributionKeys: string[]
  ): Promise<CreditSetupRuleMetricValueScopeRow[]>

  abstract getCreditedMetricValues(
    requests: CreditedMetricValueRequest[]
  ): Promise<CreditedMetricValueRow[]>

  abstract getCreditedTargetMetricValueAggregates(
    requests: CreditedMetricValueRequest[]
  ): Promise<CreditedTargetMetricValueAggregate[]>

  abstract saveMetricValues(
    source: Enums<'plan_source'>,
    values: TablesInsert<'metric_values'>[]
  ): Promise<Error | Tables<'metric_values'>[]>

  abstract reconcileCreditSetupRuleMetricValues(
    reconciliations: CreditSetupRuleMetricValueReconciliation[],
    affectedScope?: PeriodRulesAffectedScopeRow[]
  ): Promise<number>

  abstract deleteMetricValues(values: MetricValueToDelete[]): Promise<Error | void>

  abstract deleteCrmRecordsAndReferences(params: {
    organizationId: number
    tableName: string
    recordIds: string[]
  }): Promise<void>

  abstract deleteDealLineItemsByProviderIds(params: {
    organizationId: number
    provider: string
    providerIds: string[]
  }): Promise<number>

  abstract getPayouts(
    source: Enums<'plan_source'>,
    recordId: number,
    compRuleId: number,
    attributionKey?: string
  ): Promise<Tables<'payouts'>[]>

  abstract getPayoutsForSimulation(args: {
    compensationRuleIds?: number[]
    effectiveDateEnd?: string
    effectiveDateStart?: string
    entityIdsBySource?: Partial<Record<Enums<'plan_source'>, number[]>>
    organizationId?: number
    profileIds?: string[]
  }): Promise<Tables<'payouts'>[]>

  abstract getCompensationRulesByIds(
    compensationRuleIds: number[]
  ): Promise<
    Pick<
      Tables<'compensation_rules'>,
      'deal_resolution_config' | 'deal_resolution_strategy' | 'id' | 'source'
    >[]
  >

  abstract getProfilesByIds(
    profileIds: string[]
  ): Promise<Pick<Tables<'profiles'>, 'email' | 'id' | 'img_url' | 'name'>[]>

  abstract getPayoutById(
    payoutId: number,
    organizationId: number
  ): Promise<Tables<'payouts'> | null>

  abstract getOrInsertStatement(params: {
    organizationId: number
    profileId: string
    periodStart: string
  }): Promise<number>

  abstract upsertManualPayout(payload: ManualPayoutUpsertPayload): Promise<Tables<'payouts'>>
  abstract getPayoutCategoryById(
    categoryId: number,
    organizationId: number
  ): Promise<Pick<Tables<'payout_categories'>, 'id' | 'organization_id'> | null>

  abstract getStatementById(
    statementId: number,
    organizationId: number
  ): Promise<Tables<'statements'> | null>

  abstract getStatementMonthRows(params: GetStatementMonthRowsParams): Promise<StatementMonthRow[]>

  abstract getStatementReadRow(
    statementId: number,
    organizationId: number
  ): Promise<StatementMonthRow | null>

  abstract getStatementPayoutTotal(params: {
    organizationId: number
    profileId: string
    periodStart: string
  }): Promise<number>

  abstract getStatementReadRelations(
    statementId: number,
    organizationId: number
  ): Promise<StatementReadRelations>

  abstract getStatementReadChanges(
    statementId: number,
    organizationId: number,
    payoutIds: number[]
  ): Promise<StatementReadChanges>

  abstract getIssueById(issueId: number, organizationId: number): Promise<Tables<'issues'> | null>

  abstract getPayoutDiscrepancyById(
    discrepancyId: number,
    organizationId: number
  ): Promise<Tables<'payout_discrepancies'> | null>

  abstract getOrganizationsWithWithheldPayouts(): Promise<number[]>

  abstract getWithheldPayouts(
    limit: number,
    offset: number,
    organizationId?: number,
    minimumCheckAgeMs?: number
  ): Promise<PayoutWithSource[]>

  abstract markPayoutsChecked(payoutIds: number[]): Promise<void>

  abstract getPayoutReleaseContext(
    payoutId: number,
    organizationId: number
  ): Promise<PayoutReleaseContext | null>

  abstract updateReleasedPayouts(payouts: PayoutRelease[]): Promise<Error | void>

  abstract getPreviousPayouts(
    profileId: string,
    periodStart: string,
    compRuleId: number,
    ltbPeriodStart: string
  ): Promise<Tables<'payouts'>[]>

  abstract getGlobalTargetsMetricsAndProfiles(
    affectedMatrics: CompositeTypes<'affected_metrics'>[]
  ): Promise<DBFunctionReturns<'get_global_target_metrics_and_profiles'>>

  abstract getCompanyTargetAffectedProfiles(
    organizationId: number,
    metricId: number,
    affectedDate: string
  ): Promise<string[]>

  abstract getCompanyTargetAffectedProfilesBatch(
    requests: {
      organizationId: number
      metricId: number
      affectedDate: string
    }[]
  ): Promise<DBFunctionReturns<'get_company_target_affected_profiles_batch'>>

  abstract loadCustomChecks(
    organizationId: number,
    source: Enums<'plan_source'>,
    onlyLoadActiveChecks?: boolean
  ): Promise<Tables<'custom_data_checks'>[]>

  abstract getNextValidators(
    payoutId: number
  ): Promise<DBFunctionReturns<'get_next_validators'> | null>

  abstract getCompRuleActiveVersion(
    compRuleId: number,
    referenceDate: string
  ): Promise<Tables<'compensation_rule_versions'> | null>

  abstract getActiveCompensationRuleVersions(
    organizationId: number,
    referenceDate: string
  ): Promise<ActiveCompensationRuleVersion[]>

  abstract getCompensationRuleVersionsForAnalysis(
    organizationId: number,
    versionIds: number[]
  ): Promise<ActiveCompensationRuleVersion[]>

  abstract getDealPayoutsForProfile(
    organizationId: number,
    dealId: number,
    profileId: string
  ): Promise<(Tables<'payouts'> & { statements?: Tables<'statements'> })[]>

  abstract getSubscriptionCompensationSourceRows(
    params: GetSubscriptionCompensationSourceRowsParams
  ): Promise<SubscriptionCompensationSourceRow[]>

  abstract hasRecentSubscriptionData(params: HasRecentSubscriptionDataParams): Promise<boolean>

  abstract getNotificationPayoutReferences(
    payoutIds: number[]
  ): Promise<NotificationPayoutReference[]>

  abstract getPayoutManualAdjustmentBreakdowns(
    organizationId: number,
    payoutIds: number[]
  ): Promise<PayoutManualAdjustmentBreakdown[]>

  abstract getNotificationStatementReferences(
    statementIds: number[]
  ): Promise<NotificationStatementReference[]>

  abstract getStatementPeriodReferences(statementIds: number[]): Promise<StatementPeriodReference[]>

  abstract getStatementReviewFlowRows(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Promise<StatementReviewFlowRow[]>

  abstract getDealMetricValuesForProfile(
    organizationId: number,
    dealId: number,
    profileId: string
  ): Promise<DBFunctionReturns<'get_deal_related_metric_values'>>

  abstract getTvpAllocation(
    profileId: string,
    organizationId: number,
    compensationRuleVersionId: number
  ): Promise<DBFunctionReturns<'get_tvp_allocations'> | null>

  abstract getLatestTvp(
    profileId: string,
    referenceDate: string,
    organizationId?: number
  ): Promise<Tables<'organizations_profiles_data'> | null>

  abstract getProfileOrganizationStartDate(
    profileId: string,
    organizationId: number
  ): Promise<string | null>

  abstract getActiveProfileDataAtDate(
    profileId: string,
    referenceDate: string,
    organizationId: number
  ): Promise<Pick<
    CurrentOrganizationProfileData,
    | 'type'
    | 'business_unit_id'
    | 'business_unit_name'
    | 'business_unit_cost_center_id'
    | 'business_unit_cost_center'
    | 'business_unit_department'
    | 'business_unit_legal_entity'
    | 'business_unit_location_country'
    | 'business_unit_location_city'
  > | null>

  abstract getTvpAllocations(
    profileId: string,
    organizationId: number
  ): Promise<DBFunctionReturns<'get_tvp_allocations'>>

  abstract getTvpDataForProfiles(
    profileIds: string[],
    referenceDate: string,
    organizationId: number
  ): Promise<Tables<'organizations_profiles_data'>[]>

  abstract getTvpAllocationsForProfiles(
    profileIds: string[],
    organizationId: number
  ): Promise<
    (Tables<'tvp_allocation'> & {
      compensation_rules: { plans: { organization_id: number } } | null
    })[]
  >

  abstract getLatestCompRuleDraftOrVersion(
    compensationRuleId: number,
    referenceDate: string,
    isDraft: boolean
  ): Promise<{
    compensationRuleDraftId: number | null
    compensationRuleVersionId: number | null
  }>

  abstract upsertTvpAllocation(params: {
    profileId: string
    compensationRuleId: number
    percentage: number
    compensationRuleDraftId?: number | null
    compensationRuleVersionId?: number | null
  }): Promise<Tables<'tvp_allocation'>>

  abstract getDraftTvpAllocations(
    compensationRuleId: number
  ): Promise<Pick<Tables<'tvp_allocation'>, 'percentage' | 'profile_id' | 'team_id'>[]>

  abstract replaceVersionTvpAllocations(params: {
    compensationRuleId: number
    compensationRuleVersionId: number
    allocations: Pick<Tables<'tvp_allocation'>, 'percentage' | 'profile_id' | 'team_id'>[]
  }): Promise<void>

  abstract setDraftTvpBased(draftId: number, isTvpBased: boolean): Promise<void>

  abstract setVersionTvpBased(versionId: number, isTvpBased: boolean): Promise<void>

  abstract getTarget(targetId: number): Promise<
    | (Tables<'targets'> & {
        metrics: Tables<'metrics'> | null
        teams_targets: Tables<'teams_targets'>[] | null
      })
    | null
  >

  abstract setTargetCreditSetupSelection(
    params: TargetCreditSetupSelectionParams
  ): Promise<TargetWithCreditSetup>

  abstract upsertTargetWithCreditSetupSelection(
    params: TargetUpsertWithCreditSetupSelectionParams
  ): Promise<TargetWithCreditSetup>

  abstract upsertTeam(team: {
    id?: number
    name: string
    description?: string | null
    currency?: string | null
    organization_id: number
  }): Promise<Tables<'teams'>>

  // Creates or updates the business-unit hierarchy root plus its cost-center
  // children. The caller passes names for reusable dimensions and the RPC
  // implementation resolves or creates the underlying records consistently.
  abstract upsertBusinessUnit(business_unit: {
    id?: number
    organization_id: number
    name: string
    cost_centers: {
      department_name: string
      legal_entity_name?: string | null
      location_country?: string | null
      location_city?: string | null
    }[]
  }): Promise<Tables<'business_units'>>

  // Removes the business unit according to the history rule enforced in SQL:
  // hard-delete it only when it has never been assigned, otherwise archive it
  // so past and current references remain meaningful.
  abstract removeBusinessUnit(
    businessUnitId: number,
    organizationId: number
  ): Promise<BusinessUnitRemovalResult>

  // Applies a dated business-unit assignment change for one or more profiles.
  // `existingEffectiveFromDate` is only used when editing an existing timeline
  // row so the SQL layer knows which dated range to replace.
  abstract assignBusinessUnitToProfiles(params: {
    organizationId: number
    businessUnitId: number | null
    businessUnitCostCenterId: number | null
    effectiveFromDate: string
    effectiveToDate: string | null
    existingEffectiveFromDate: string | null
    extendPreviousAssignment: boolean
    bringForwardNextAssignment: boolean
    profileIds: string[]
  }): Promise<Tables<'organizations_profiles'>[]>

  // Returns the persisted business-unit timeline rows for one profile,
  // already reduced to assignment changes rather than the full snapshot stream.
  abstract getProfileBusinessUnitsHistory(
    organizationId: number,
    profileId: string
  ): Promise<BusinessUnitTimelineHistoryRow[]>

  // Returns a SQL-backed preview payload for one draft timeline edit. The
  // overlap semantics come from the same daterange engine used by the write
  // path so the UI preview cannot drift from persistence behavior.
  abstract previewBusinessUnitTimeline(params: {
    organizationId: number
    profileId: string
    businessUnitId: number | null
    businessUnitCostCenterId: number | null
    effectiveFromDate: string
    effectiveToDate: string | null
    existingEffectiveFromDate: string | null
    extendPreviousAssignment: boolean
    bringForwardNextAssignment: boolean
  }): Promise<BusinessUnitTimelinePreviewResult>

  abstract updateTeamsTags(teamId: number, tags: string[]): Promise<void>
  abstract getMetricUsage(metricId: number): Promise<MetricUsage>

  abstract listCreditSetups(params: CreditSetupListParams): Promise<Tables<'credit_setups'>[]>

  abstract listCreditSetupsByMetricIds(
    params: CreditSetupListByMetricIdsParams
  ): Promise<Tables<'credit_setups'>[]>

  abstract getCreditSetup(
    setupId: number,
    organizationId: number,
    sourceMetricId: number
  ): Promise<Tables<'credit_setups'> | null>

  abstract getCreditSetupUsage(
    organizationId: number,
    creditSetupId: number
  ): Promise<CreditSetupUsage>

  abstract createCreditSetup(setup: TablesInsert<'credit_setups'>): Promise<Tables<'credit_setups'>>

  abstract updateCreditSetup(params: CreditSetupUpdateParams): Promise<Tables<'credit_setups'>>

  abstract archiveCreditSetup(
    setupId: number,
    organizationId: number,
    sourceMetricId: number
  ): Promise<Tables<'credit_setups'>>

  abstract listCreditSetupRules(
    params: CreditSetupRuleListParams
  ): Promise<Tables<'credit_setup_rules'>[]>

  abstract listCreditSetupRulesPage(params: CreditSetupRulePageParams): Promise<CreditSetupRulePage>

  abstract getCreditSetupRule(
    ruleId: number,
    organizationId: number,
    creditSetupId: number
  ): Promise<Tables<'credit_setup_rules'> | null>

  abstract createCreditSetupRule(
    rule: TablesInsert<'credit_setup_rules'>
  ): Promise<Tables<'credit_setup_rules'>>

  abstract updateCreditSetupRule(
    params: CreditSetupRuleUpdateParams
  ): Promise<Tables<'credit_setup_rules'>>

  abstract archiveCreditSetupRule(
    ruleId: number,
    organizationId: number,
    creditSetupId: number
  ): Promise<Tables<'credit_setup_rules'>>

  abstract getCreditSetupRuleUsage(
    params: CreditSetupRuleUsageParams
  ): Promise<CreditSetupRuleAssignment[]>

  abstract listCreditSetupRuleAssignments(
    params: CreditSetupRuleAssignmentListParams
  ): Promise<CreditSetupRuleAssignment[]>

  abstract listCreditSetupRuleAssignmentsForRules(
    params: CreditSetupRuleAssignmentBatchParams
  ): Promise<CreditSetupRuleAssignment[]>

  abstract getCreditSetupRuleAssignment(
    assignmentId: number,
    organizationId: number,
    creditSetupId: number
  ): Promise<CreditSetupRuleAssignment | null>

  abstract createCreditSetupRuleAssignment(
    params: CreditSetupRuleAssignmentReplaceParams
  ): Promise<CreditSetupRuleAssignment>

  abstract endCreditSetupRuleAssignment(
    params: CreditSetupRuleAssignmentUpdateParams
  ): Promise<CreditSetupRuleAssignment>

  abstract updateCreditSetupRuleAssignment(
    params: CreditSetupRuleAssignmentEditParams
  ): Promise<CreditSetupRuleAssignment>

  abstract replaceCreditSetupRuleAssignment(
    params: CreditSetupRuleAssignmentReplaceParams
  ): Promise<CreditSetupRuleAssignment[]>

  abstract getDailyMetrics(params: {
    organizationId: number
    metricIds: number[]
    teamIds?: number[]
    startDate: string
    endDate: string
  }): Promise<DBFunctionReturns<'get_daily_metrics'>>

  abstract getTargetUsage(targetId: number): Promise<TargetUsage>

  abstract getTeamUsage(teamId: number): Promise<TeamUsage>
  abstract getTeamHasMetricValues(teamId: number): Promise<boolean>
  abstract getTeamDeletionBlockers(teamId: number): Promise<TeamDeletionBlockers>
  abstract unlinkTeamFromPlan(args: UnlinkTeamFromPlanArgs): Promise<void>
  abstract unlinkTeamFromTarget(
    organizationId: number,
    teamId: number,
    targetId: number
  ): Promise<void>

  abstract findMatchingDeals(
    organizationId: number,
    dealLinkField: string,
    dealLinkValue: string,
    referenceDate: string
  ): Promise<DBFunctionReturns<'find_matching_deals'>>

  abstract getActiveCurrencies(organizationId: number): Promise<string[]>

  abstract saveMetricValueCurrencies(conversions: MetricValueCurrencySnapshot[]): Promise<number>
  abstract startMetricValueCurrencyBackfill(
    organizationId: number,
    currency: string
  ): Promise<MetricValueCurrencyBackfillProgress>
  abstract reconcileMetricValueCurrencyDimensionsChunk(params: {
    organizationId: number
    currency: string
    afterMetricValueId: number
    throughMetricValueId: number
    limit: number
  }): Promise<MetricValueCurrencyReconciliationChunk>
  abstract getMetricValuesForCurrencyBackfill(params: {
    organizationId: number
    currency: string
    afterMetricValueId: number
    throughMetricValueId: number
    limit: number
  }): Promise<MetricValueCurrencyBackfillPage>
  abstract updateMetricValueCurrencyBackfillProgress(
    organizationId: number,
    currency: string,
    progress: MetricValueCurrencyProgressUpdate
  ): Promise<MetricValueCurrencyBackfillProgress>
  abstract verifyMetricValueCurrencyBackfill(
    organizationId: number,
    currency: string
  ): Promise<MetricValueCurrencyBackfillProgress>

  abstract getTargetsMissingAmounts(
    organizationId: number
  ): Promise<DBFunctionReturns<'get_targets_with_missing_amounts'>>

  abstract getDataIssuesSummary(params: {
    organizationId: number
    tableName: string
    periodStart?: string
    periodEnd?: string
  }): Promise<Json>

  abstract getStatementPeriodCutDays(
    organizationId: number,
    currentPeriodDate: string
  ): Promise<number[]>

  abstract getReportedIssuesCount(organizationId: number): Promise<number>

  abstract getUnreviewedDiscrepanciesCount(organizationId: number): Promise<number>

  abstract getUnreviewedDiscrepancies(
    organizationId: number
  ): Promise<(Tables<'payout_discrepancies'> & { payouts?: Tables<'payouts'> | null })[]>

  abstract getUpdatesOnHoldScaffoldRows(organizationId: number): Promise<UpdateOnHoldScaffoldRow[]>

  abstract getUpdatesOnHoldReviewRows(
    organizationId: number,
    updateIds?: number[]
  ): Promise<UpdateOnHoldReviewRow[]>

  abstract previewUpdatesOnHoldResolution(
    organizationId: number,
    decisions: UpdateOnHoldDecisionInput[]
  ): Promise<UpdateOnHoldPreview>

  abstract resolveUpdatesOnHoldResolution(
    organizationId: number,
    decisions: UpdateOnHoldDecisionInput[],
    fingerprint: string
  ): Promise<UpdateOnHoldCommittedResult>

  abstract getAppliedDiscrepancies(
    organizationId: number
  ): Promise<(Tables<'payout_discrepancies'> & { payouts?: Tables<'payouts'> | null })[]>

  abstract getPayoutDiscrepancyRecalculationReasons(
    organizationId: number,
    discrepancyIds: number[]
  ): Promise<PayoutDiscrepancyRecalculationReason[]>

  abstract getRecordRecalculationHistory(params: {
    organizationId: number
    source: RecordInvestigationSource
    recordId: number
  }): Promise<RecordRecalculationHistoryEntry[]>

  abstract getMeaningfulAuditChanges(params: {
    tableName: MeaningfulAuditChangeTableName
    idColumn: string
    organizationId: number
    recordIds: number[]
    beforeTimestamp: string
  }): Promise<MeaningfulAuditChange[]>

  abstract getPayoutRuleScopedMetricContributions(
    payoutId: number,
    sourceIds: number[]
  ): Promise<PayoutRuleScopedMetricContribution[]>

  abstract getPayoutRuleMetricFieldReferences(
    payoutId: number
  ): Promise<PayoutRuleMetricFieldReference[]>

  abstract getDiscrepanciesForPayout(payoutId: number): Promise<
    (Tables<'payout_discrepancies'> & {
      existing_statement?: {
        period_start: string
        period_end: string
        cut_period: string | null
        status: string
      } | null
      calculated_statement?: {
        period_start: string
        period_end: string
        cut_period: string | null
        status: string
      } | null
      adjustment_payouts?: Array<{
        value: number
        currency: string | null
        statement_id: number | null
        statements?: {
          period_start: string
          period_end: string
          cut_period: string | null
          status: string
        } | null
      }>
    })[]
  >

  abstract getDiscrepancyIndicators(
    organizationId: number
  ): Promise<
    Pick<
      Tables<'payout_discrepancies'>,
      'payout_id' | 'reviewed' | 'resolution_type' | 'discrepancy_type'
    >[]
  >

  abstract applyValueDiscrepancy(
    discrepancyId: number,
    adjustmentPayoutData: ValueDiscrepancyAdjustmentInput
  ): Promise<void>

  abstract applyStatementDiscrepancy(
    discrepancyId: number,
    payoutId: number,
    newStatementId: number
  ): Promise<void>

  abstract resolvePayoutDiscrepancy(
    discrepancyId: number,
    action: 'apply' | 'discard' | 'next_statement'
  ): Promise<Record<string, unknown>>

  abstract getNextStatementForPayoutDiscrepancy(discrepancyId: number): Promise<{
    statement_id: number | null
    period_start: string
    period_end: string
    status: Enums<'statement_status'> | null
  } | null>

  abstract markDiscrepancyAsReviewed(discrepancyId: number): Promise<void>

  abstract getStatementIdForAccrualDate(
    organizationId: number,
    profileId: string,
    accrualDate: string
  ): Promise<number | null>

  abstract resetPayoutValidation(
    payoutId: number,
    discrepancyId: number,
    newValue: number
  ): Promise<void>
  abstract revokePayoutValidations(payoutId: number): Promise<void>

  // Reverting into an approved statement reopens it and clears its approvals.
  abstract revertStatementDiscrepancy(
    discrepancyId: number,
    payoutId: number,
    targetStatementId: number
  ): Promise<StatementDiscrepancyRevertResult>

  abstract getUnreviewedDiscrepanciesCountForStatement(statementId: number): Promise<number>

  abstract getOrganizationValidationProcessConfig(
    organizationId: number
  ): Promise<ValidationProcessOrganizationConfig>

  abstract getValidationStepEntities(organizationId: number): Promise<ValidationProcessStepEntity[]>

  abstract getValidationProcessProfilesByIds(
    profileIds: string[]
  ): Promise<Pick<Tables<'profiles'>, 'id' | 'name' | 'email'>[]>

  abstract countCompletedValidationPayouts(
    params: GetValidationProcessPayoutParams
  ): Promise<number>

  abstract countPendingValidationPayouts(params: GetValidationProcessPayoutParams): Promise<number>

  abstract listPendingValidationPayoutReviewerRows(
    params: GetValidationProcessPayoutParams & GetValidationProcessRowsParams
  ): Promise<ValidationProcessReviewerRow[]>

  abstract listPendingValidationPayoutSamples(
    params: GetValidationProcessPayoutParams & { limit?: number }
  ): Promise<ValidationProcessRawPayoutSample[]>

  abstract listCompletedValidationPayoutSamples(
    params: GetValidationProcessPayoutParams & { limit?: number }
  ): Promise<ValidationProcessRawPayoutSample[]>

  abstract countCompletedValidationStatements(
    params: GetValidationProcessStatementParams
  ): Promise<number>

  abstract countPendingValidationStatements(
    params: GetValidationProcessStatementParams
  ): Promise<number>

  abstract countOpenNotPendingValidationStatements(
    params: GetValidationProcessStatementParams
  ): Promise<number>

  abstract listPendingValidationStatementReviewerRows(
    params: GetValidationProcessStatementParams & GetValidationProcessRowsParams
  ): Promise<ValidationProcessReviewerRow[]>

  abstract listPendingValidationStatementSamples(
    params: GetValidationProcessStatementParams & { limit?: number }
  ): Promise<ValidationProcessRawStatementSample[]>

  abstract listOpenNotPendingValidationStatementSamples(
    params: GetValidationProcessStatementParams & { limit?: number }
  ): Promise<ValidationProcessRawStatementSample[]>

  abstract listCompletedValidationStatementSamples(
    params: GetValidationProcessStatementParams & { limit?: number }
  ): Promise<ValidationProcessRawStatementSample[]>

  abstract getPendingPayoutValidationsCount(
    organizationId: number,
    monthStarts: string[],
    validatorUserId?: string
  ): Promise<number>

  abstract getPendingStatementApprovalsCount(
    organizationId: number,
    periodStart: string,
    periodEnd: string,
    approverUserId?: string
  ): Promise<number>

  abstract getPayoutAnalytics(params: PayoutAnalyticsParams): Promise<PayoutAnalytics>

  abstract getGeneratedPayoutsAnalytics(
    params: PayoutAnalyticsParams
  ): Promise<GeneratedPayoutsAnalytics>

  abstract getPayablePayoutsAnalytics(
    params: PayoutAnalyticsParams
  ): Promise<PayablePayoutsAnalytics>

  abstract getManualPayoutAnalytics(params: PayoutAnalyticsParams): Promise<ManualPayoutsAnalytics>

  abstract getPayoutAdjustmentAnalytics(
    params: PayoutAnalyticsParams
  ): Promise<PayoutAdjustmentsAnalytics>

  abstract ensureStatementsForPeriodRange(
    organizationId: number,
    startDate: string,
    endDate: string
  ): Promise<number>
  abstract getStatementApproval(
    statementId: number,
    actorId: string
  ): Promise<Tables<'statement_approvals'> | null>
  abstract insertStatementApproval(params: {
    statementId: number
    actorId: string
  }): Promise<Tables<'statement_approvals'>>
  abstract createStatementApprovalNotification(params: {
    organizationId: number
    statementId: number
    actorId: string
  }): Promise<void>
  abstract insertStatementIssue(params: {
    organizationId: number
    statementId: number
    actorId: string
  }): Promise<Tables<'issues'>>
  abstract createStatementIssueNotification(params: {
    organizationId: number
    issueId: number
    actorId: string
  }): Promise<void>
  abstract changeStatementPaymentStatus(
    params: ChangeStatementPaymentStatusParams
  ): Promise<Pick<Tables<'statements'>, 'id' | 'status'> | null>
  abstract createStatementPaidNotifications(params: {
    organizationId: number
    actorId: string
    statementIds: number[]
  }): Promise<void>
  abstract startStatementsReview(
    organizationId: number,
    statementIds: number[]
  ): Promise<StartStatementsReviewResult>
  abstract getStatementResetEligibility(
    statementIds: number[]
  ): Promise<StatementResetEligibility[]>

  abstract resetStatementToPendingApproval(statementId: number): Promise<void>

  abstract getUsersWithoutTeamCount(organizationId: number): Promise<number>
  abstract getOrganizationSetupFlags(organizationId: number): Promise<OrganizationSetupFlags | null>
  abstract updateOrganizationSetupFlags(
    organizationId: number,
    flags: Partial<OrganizationSetupFlags>
  ): Promise<OrganizationSetupFlags>
  abstract getOrganizationMemberCountsByRole(
    organizationId: number
  ): Promise<Array<{ role: Tables<'organizations_profiles'>['role']; count: number }>>
  abstract getPendingInvitationCount(organizationId: number): Promise<number>
  abstract getTeamCount(organizationId: number): Promise<number>
  abstract getPlanCount(organizationId: number): Promise<number>
  abstract getTargetCount(organizationId: number): Promise<number>
  abstract getBusinessUnitCount(organizationId: number): Promise<number>
  abstract getMultiTeamUsersCount(organizationId: number): Promise<number>
  abstract getTeamsWithoutMembersCount(organizationId: number): Promise<number>
  abstract getPlansWithoutLinkedTeamsCount(organizationId: number): Promise<number>
  abstract getTargetsWithoutLinkedTeamsCount(organizationId: number): Promise<number>

  abstract listOrganizationTeamsSetup(organizationId: number): Promise<TeamSetupSummaryRow[]>

  abstract listOrganizationTeamsWithoutMembersSetup(
    organizationId: number
  ): Promise<TeamSetupSummaryRow[]>

  abstract listTeamSetupMembers(
    organizationId: number,
    teamId: number
  ): Promise<TeamSetupMemberRow[]>

  abstract listTeamSetupPlans(
    organizationId: number,
    teamId: number
  ): Promise<TeamSetupPlanSummaryRow[]>

  abstract listTeamSetupTargets(
    organizationId: number,
    teamId: number
  ): Promise<TeamSetupTargetSummaryRow[]>

  abstract listTeamMembershipHistorySetup(
    organizationId: number,
    teamId: number
  ): Promise<TeamMembershipHistorySetupRow[]>

  abstract listMemberMembershipHistorySetup(
    organizationId: number,
    profileId: string
  ): Promise<TeamMembershipHistorySetupRow[]>

  abstract listProfileCurrentTeamMembershipsSetup(
    organizationId: number,
    profileId: string
  ): Promise<CurrentTeamMembershipSetupRow[]>

  abstract saveUserEditTeamMembershipRangeSetup(
    args: SaveUserEditTeamMembershipRangeSetupParams
  ): Promise<void>

  abstract setTeamArchivedState(
    organizationId: number,
    teamId: number,
    archived: boolean
  ): Promise<Tables<'teams'> | null>

  abstract deleteTeamSetup(organizationId: number, teamId: number): Promise<void>

  abstract upsertTeamMembershipRoleAccess(args: {
    organizationId: number
    teamId: number
    profileId: string
    role?: Enums<'team_role'>
    fullViewAccess?: boolean
  }): Promise<Tables<'teams_profiles'> | null>

  abstract moveProfilesToTeams(args: {
    organizationId: number
    moves: Array<{
      profileId: string
      teamId: number
      date: string
      role: Enums<'team_role'>
      fullViewAccess: boolean
    }>
  }): Promise<void>

  abstract reconcileTeamMembershipMoveEventDates(args: {
    organizationId: number
    profileId: string
    newTeamId: number
    oldTeamId: number | null
    effectiveDate: string
  }): Promise<void>

  abstract removeProfileFromTeamSetup(args: {
    organizationId: number
    teamId: number
    profileId: string
    effectiveDate: string
  }): Promise<boolean>

  abstract listOrganizationMembersSetup(
    organizationId: number
  ): Promise<OrganizationMemberSetupRow[]>

  abstract listOrganizationInvitationsSetup(
    organizationId: number
  ): Promise<OrganizationInvitationSetupRow[]>

  abstract listUsersWithoutTeamsSetup(organizationId: number): Promise<OrganizationMemberSetupRow[]>

  abstract listMultiTeamMembersSetup(organizationId: number): Promise<OrganizationMemberSetupRow[]>

  abstract updateOrganizationMemberRole(args: {
    organizationId: number
    profileId: string
    role: Enums<'org_role'>
  }): Promise<Tables<'organizations_profiles'> | null>

  abstract setOrganizationMemberArchived(args: {
    organizationId: number
    profileId: string
    archived: boolean
  }): Promise<Tables<'organizations_profiles'> | null>

  abstract isOrganizationAdmin(profileId: string, organizationId: number): Promise<boolean>

  abstract getCompensationRuleAuthoringReferences(args: {
    organizationId: number
    planId: number
    compensationRuleId?: number
    source?: Enums<'plan_source'>
    kpisTableId?: number
    customRecordTableId?: number
    metricIds?: number[]
    fieldNames?: string[]
    targetIds?: number[]
  }): Promise<CompensationRuleAuthoringReferences>

  abstract searchCompensationRuleAuthoringReferences(args: {
    organizationId: number
    planId: number
    source?: Enums<'plan_source'>
    kpisTableId?: number
    customRecordTableId?: number
    query?: string
    limit?: number
  }): Promise<CompensationRuleAuthoringReferenceSearchResult>

  abstract startCompensationRuleAuthoringSession(
    args: CompensationRuleAuthoringSessionRpcParams
  ): Promise<Json>

  abstract readCompensationRuleAuthoringSession(sessionId: string): Promise<Json>
  abstract listCompensationRuleDraftHistory(args: {
    organizationId: number
    compensationRuleId: number
    limit?: number
  }): Promise<CompensationRuleDraftHistoryRow[]>

  abstract recordCompensationRuleDraftHistory(args: {
    organizationId: number
    compensationRuleId: number
    event: CompensationRuleDraftHistoryEvent
    maxEntries?: number
  }): Promise<CompensationRuleDraftHistoryRecord | null>

  abstract readCompensationRuleAuthoringSessionForActor(
    actorId: string,
    sessionId: string
  ): Promise<Json>

  abstract updateCompensationRuleAuthoringSession(
    args: CompensationRuleAuthoringSessionUpdateRpcParams
  ): Promise<Json>

  abstract updateCompensationRuleAuthoringSessionForActor(
    args: CompensationRuleAuthoringSessionUpdateRpcParams & { actorId: string }
  ): Promise<Json>

  abstract commitCompensationRuleAuthoringSession(
    args: CompensationRuleAuthoringCommitRpcParams
  ): Promise<Json>

  abstract commitCompensationRuleAuthoringSessionForActor(
    args: CompensationRuleAuthoringCommitRpcParams & { actorId: string }
  ): Promise<Json>

  abstract getPlanForDuplication(
    planId: number,
    organizationId: number
  ): Promise<PlanDuplicationRecord | null>

  abstract listPlanLabels(organizationId: number): Promise<string[]>

  abstract createPlan(plan: TablesInsert<'plans'>): Promise<Pick<Tables<'plans'>, 'id' | 'label'>>

  abstract createCompensationRule(
    rule: TablesInsert<'compensation_rules'>
  ): Promise<Pick<Tables<'compensation_rules'>, 'id'>>

  abstract createCompensationRuleDraft(
    draft: TablesInsert<'compensation_rule_drafts'>
  ): Promise<Pick<Tables<'compensation_rule_drafts'>, 'id'>>

  abstract linkTeamToPlan(args: LinkTeamToPlanArgs): Promise<void>
  abstract listPlanTeamHistory(
    organizationId: number,
    planId: number
  ): Promise<PlanTeamHistoryLink[]>
  abstract upsertPlanTeamHistory(args: UpsertPlanTeamHistoryArgs): Promise<PlanTeamHistoryLink>
  abstract deletePlanTeamHistory(organizationId: number, historyId: number): Promise<void>
  abstract replacePlanTeamHistory(args: ReplacePlanTeamHistoryArgs): Promise<PlanTeamHistoryLink[]>
  abstract linkTeamToTarget(
    organizationId: number,
    teamTarget: TablesInsert<'teams_targets'>
  ): Promise<void>
  abstract listPlanTeamLinksByPlanIds(
    organizationId: number,
    planIds: number[]
  ): Promise<CurrentPlanTeamLink[]>
  abstract listPlanTeamLinksByOrganization(organizationId: number): Promise<CurrentPlanTeamLink[]>
  abstract listPlanTeamLinksByTeamIds(
    organizationId: number,
    teamIds: number[]
  ): Promise<CurrentPlanTeamLink[]>
  abstract listTargetTeamLinksByTargetIds(
    organizationId: number,
    targetIds: number[]
  ): Promise<Array<Pick<Tables<'teams_targets'>, 'target_id' | 'team_id'>>>
  abstract listTargetTeamLinksByOrganization(
    organizationId: number
  ): Promise<Array<Pick<Tables<'teams_targets'>, 'target_id' | 'team_id'>>>
  abstract listTargetTeamLinksByTeamIds(
    organizationId: number,
    teamIds: number[]
  ): Promise<Array<Pick<Tables<'teams_targets'>, 'target_id' | 'team_id'>>>

  abstract deleteCompensationRuleDraft(draftId: number, compensationRuleId: number): Promise<void>

  abstract deleteCompensationRule(ruleId: number, planId: number): Promise<void>

  abstract deletePlan(planId: number, organizationId: number): Promise<void>

  // ---- Chat history ----
  abstract claimFlipperSlackEvent(params: {
    eventId: string
    organizationId: number | null
    profileId: string | null
    teamId: string
    channelId: string | null
    rootThreadTs: string | null
    eventTs: string | null
    eventType: string
    payload: Json
  }): Promise<boolean>
  abstract releaseFlipperSlackEvent(params: { eventId: string }): Promise<void>
  abstract getActiveFlipperSlackEvent(params: {
    organizationId: number
    teamId: string
    channelId: string
    rootThreadTs: string
  }): Promise<{
    event_id: string
    status: string
  } | null>

  abstract updateFlipperSlackEvent(params: {
    eventId: string
    status?: 'received' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'expired'
    responseTs?: string | null
    feedback?: 'helpful' | 'not_helpful' | null
    errorMessage?: string | null
  }): Promise<void>

  abstract getFlipperSlackThread(params: {
    organizationId: number
    teamId: string
    channelId: string
    rootThreadTs: string
  }): Promise<{
    id: string
    organization_id: number
    profile_id: string
    team_id: string
    channel_id: string
    root_thread_ts: string
    conversation_id: string
    title: string | null
    latest_event_id: string | null
  } | null>

  abstract createFlipperSlackThread(params: {
    organizationId: number
    profileId: string
    teamId: string
    channelId: string
    rootThreadTs: string
    title?: string
  }): Promise<{
    id: string
    conversationId: string
    profileId: string
  }>

  abstract updateFlipperSlackThread(params: {
    organizationId: number
    teamId: string
    channelId: string
    rootThreadTs: string
    title?: string
    latestEventId?: string | null
  }): Promise<void>

  abstract getActiveFlipperSlackSessionDelegation(params: {
    organizationId: number
    slackTeamId: string
    slackUserId: string
  }): Promise<{
    id: string
    organization_id: number
    profile_id: string
    slack_team_id: string
    slack_user_id: string
    encrypted_session: string
    pending_revoke_encrypted_session: string | null
    session_expires_at: string
    version: number
  } | null>

  abstract claimFlipperSlackSessionDelegation(params: {
    organizationId: number
    profileId: string
    slackTeamId: string
    slackUserId: string
    encryptedSession: string
    sessionExpiresAt: string
  }): Promise<{
    accepted: boolean
    delegation: {
      id: string
      organization_id: number
      profile_id: string
      slack_team_id: string
      slack_user_id: string
      encrypted_session: string
      pending_revoke_encrypted_session: string | null
      session_expires_at: string
      version: number
    }
    replacedEncryptedSession: string | null
  }>

  abstract clearFlipperSlackPendingSessionRevocation(params: {
    delegationId: string
    version: number
    encryptedSession: string
  }): Promise<boolean>

  abstract rotateFlipperSlackSessionDelegation(params: {
    delegationId: string
    version: number
    encryptedSession: string
    sessionExpiresAt: string
  }): Promise<boolean>

  abstract revokeFlipperSlackSessionDelegation(params: {
    organizationId: number
    profileId: string
    slackTeamId: string
    slackUserId: string
  }): Promise<void>

  abstract createChatConversation(params: {
    organizationId: number
    profileId: string
    title?: string
    chatType?: string
    metadata?: Record<string, unknown>
  }): Promise<{ id: string }>

  abstract getChatConversation(params: {
    conversationId: string
    organizationId: number
    profileId: string
  }): Promise<{
    id: string
    organization_id: number
    profile_id: string
    title: string | null
    chat_type: string
    created_at: string
    updated_at: string
  } | null>

  abstract getChatConversations(params: {
    organizationId: number
    profileId: string
    chatType?: string
    limit?: number
    offset?: number
  }): Promise<
    {
      id: string
      title: string | null
      chat_type: string
      created_at: string
      updated_at: string
    }[]
  >

  abstract getChatConversationPreviews(
    conversationIds: string[]
  ): Promise<Record<string, string | null>>

  abstract updateChatConversation(
    params: {
      conversationId: string
      organizationId: number
      profileId: string
    },
    updates: { title?: string; metadata?: Record<string, unknown> }
  ): Promise<void>

  abstract deleteChatConversation(params: {
    conversationId: string
    organizationId: number
    profileId: string
  }): Promise<void>

  abstract getChatMessages(params: {
    conversationId: string
    organizationId: number
    profileId: string
  }): Promise<
    {
      id: string
      role: string
      content: string
      parts: unknown
      metadata: unknown
      created_at: string
    }[]
  >

  abstract saveChatMessages(
    messages: {
      conversation_id: string
      role: string
      content: string
      parts?: unknown
      metadata?: Record<string, unknown>
    }[]
  ): Promise<{ id: string }[]>

  abstract saveChatEmbedding(params: {
    conversationId: string
    organizationId: number
    content: string
    embedding: number[]
    metadata?: Record<string, unknown>
  }): Promise<{ id: string }>

  abstract searchChatEmbeddings(params: {
    queryEmbedding: number[]
    organizationId: number
    profileId: string
    matchThreshold?: number
    matchCount?: number
  }): Promise<
    {
      id: string
      conversation_id: string
      content: string
      similarity: number
    }[]
  >
}
